// Faithful browser port of the marabot-video2 slideshow compositor, used to
// render a pixel-accurate live preview of the V2 slideshow templates while the
// user is still configuring them. It mirrors, as closely as a canvas allows:
//   - the shape + stencil(clip) model (slideshow/motion_graphics/shapes.py,
//     animation.py),
//   - step/tween evaluation with ease_in_out smoothstep (tweens.py),
//   - the per-template motion-graphics presets (scroll, slide_in,
//     scroll_dual_tone, scroll_portrait, modern),
//   - rounded rects, linear gradients and contain/cover/trim image shapes
//     (shapes.py: Rectangle radius, LinearGradient, ImageShape),
//   - fitText auto-sizing (motion_graphics_preset.py),
//   - and the intro/outro composite + cover-cropped photo slides
//     (slideshow_template.py).
//
// The ONLY unavoidable divergence from the real (FreeType/PIL) render is
// sub-pixel text metrics; geometry, colour, layout and animation state are
// reproduced exactly. The preview shows three frozen frames per template
// (intro / a middle photo / outro) rather than animating.
//
// Coordinate system matches Cairo/canvas: origin top-left, +x right, +y down.

import { parseSlideshowFont, SlideshowFontMeta } from "../../../../utils/slideshowFont";

export type Rgba = [number, number, number, number];
export type Color = string | Rgba;

const FPS = 60;
const PADDING: [number, number, number, number] = [30, 30, 30, 30];
const DEFAULT_COLORS: [string, string, string] = ["#1c3657", "#e0ba65", "#fefefe"];

// Modern (template5) bookend headlines — sized together so the intro & closing
// titles render at the same size (modern.py `_BOOKEND_HEADLINES`).
const MODERN_INTRO_HEADLINE = "JUST LISTED";
const MODERN_OUTRO_HEADLINE = "LET'S CONNECT";
const MODERN_BOOKENDS = [MODERN_INTRO_HEADLINE, MODERN_OUTRO_HEADLINE];

// Per-role font weights for Modern (Slideshow.font_for preferences → numeric).
// The font face is loaded across its full variable axis, so a variable family
// (e.g. Inter) renders these directly; a static family clamps to its nearest.
const MODERN_HEADLINE_WEIGHT = 800;
const MODERN_BODY_WEIGHT = 500;
const MODERN_CAPTION_HEADER_WEIGHT = 600;
const MODERN_CAPTION_SUB_WEIGHT = 400;

// A subheader stays on ONE line whenever the whole video's subheaders can share a
// uniform size at least this tall; only below it do the long ones wrap to two
// balanced lines (motion_graphics_preset.SUB_MIN_SINGLE_PX).
const SUB_MIN_SINGLE_PX = 18;

// How much bigger a header must be than its own subheader. Uncrushed caption sets
// already land at 1.6-2.2x on their own, so this pulls long-caption videos into the
// same band rather than imposing a new look (CAPTION_MIN_HEADER_RATIO).
const CAPTION_MIN_HEADER_RATIO = 1.5;

// Spotlight (template7/9) per-role font weights (Slideshow.font_for → numeric):
// headline/stamp = ExtraBold, name/tag = Bold, body/closing-name = SemiBold,
// sub = Regular.
const SPOTLIGHT_HEADLINE_WEIGHT = 800;
const SPOTLIGHT_NAME_WEIGHT = 700;
const SPOTLIGHT_BODY_WEIGHT = 600;
const SPOTLIGHT_SUB_WEIGHT = 400;

// Portrait Modern (template8) caption header — a notch heavier (Bold) than the
// landscape Modern caption header, per modern_portrait.py.
const PORTRAIT_CAPTION_HEADER_WEIGHT = 700;

// Magazine (template6): the photo occupies the left this fraction; the solid
// brand panel takes the rest (magazine.py `_PANEL_FRAC`).
const MAGAZINE_PANEL_FRAC = 0.62;

export type TemplateId =
    | "template1"
    | "template2"
    | "template3"
    | "template4"
    | "template5"
    | "template6"
    | "template7"
    | "template8"
    | "template9"
    | "template10"
    | "template11";

// Ken Burns tour slides open from a punch-in and settle at the full framing
// (video2 template_10/template_11 ZOOM_START/ZOOM_END). Drawn statically, a
// moving slide shows the framing it opens from — which is also where video2
// freezes the opening slide; only the closing slide sits at the settled framing.
// The Slide frame is additionally played back live (see SlideshowPreview), which
// is what these are exported for.
export const KEN_BURNS_ZOOM_START = 1.1;
export const KEN_BURNS_ZOOM_END = 1.0;

// A tour slide's clip runs its nominal 4s plus the 1s cross-dissolve it has to
// keep supplying picture for, and the zoom ramps linearly across the whole clip
// (video2 add_zoom_image). Playback loops over that same span so the preview
// drifts at the rendered rate rather than a made-up one.
export const KEN_BURNS_SLIDE_SECONDS = 5;

export interface PreviewConfig {
    colors?: [string, string, string];
    font: string;
    firstname: string;
    lastname: string;
    contactNumber: string;
    texts: { header: string; subHeader: string }[];
    imageCount: number;
    // MB-1490: whether the opener may claim the listing is new. Optional so an
    // older API response (field absent) keeps today's badge — see
    // showRecencyBadge for the exact semantics.
    recentlyListed?: boolean;
}

// The compositor's flag semantics (template_1.py `recently_listed`): opt-out
// only. Absent AND explicit null both mean ON, so callers that never send the
// key are unchanged and "unknown" never silently drops the claim; only a
// literal false turns the recency elements off.
export const showRecencyBadge = (
    cfg: Pick<PreviewConfig, "recentlyListed">,
): boolean => cfg.recentlyListed !== false;

// ---------------------------------------------------------------------------
// Tween / step model (tweens.py + animation.py)
// ---------------------------------------------------------------------------

type Ease = (t: number) => number;
const linear: Ease = (t) => t;
const easeInOut: Ease = (t) => t * t * (3 - 2 * t);

interface TweenSpec {
    mode: "by" | "to";
    // Numeric geometry tween, or an absolute [r,g,b,a] target for colour fades
    // (magazine.py `_fade` tweens the `color` prop channel-by-channel).
    val: number | Rgba;
    ease: Ease;
}
const by = (delta: number, ease: Ease = linear): TweenSpec => ({ mode: "by", val: delta, ease });
const to = (end: number | Rgba, ease: Ease = linear): TweenSpec => ({ mode: "to", val: end, ease });

interface StepSpec {
    duration: number;
    tweens?: Record<string, TweenSpec>;
}
const step = (duration: number, tweens?: Record<string, TweenSpec>): StepSpec => ({ duration, tweens });

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

type ShapeKind = "rect" | "text" | "image" | "gradient" | "circle";

interface ShapeInit {
    x: number;
    y: number;
    width?: number;
    height?: number;
    text?: string;
    // text (kind === "text"): font size. circle (kind === "circle"): radius, with
    // x,y as the centre (shapes.py `Circle.size`).
    size?: number;
    color: Color;
    borderWidth?: number;
    borderColor?: Color;
    // Rounded-rect corner radius (Rectangle.radius); 0 / omitted = sharp corners.
    radius?: number;
    // Per-shape font weight override (mirrors Slideshow.font_for role weights);
    // omitted = the selected font's own weight. Never tweened.
    weight?: number;
    // gradient (kind === "gradient"): second stop colour + the start/end vector
    // as fractions (0..1) of the rect (LinearGradient.x1..y2).
    color2?: Color;
    gx1?: number;
    gy1?: number;
    gx2?: number;
    gy2?: number;
    // image (kind === "image"): which loaded asset to paint, the fit mode, and
    // an opacity. `trim` contain-fits after cropping the logo's uniform border.
    imageRef?: "logo" | "person";
    fit?: "contain" | "cover" | "trim";
    opacity?: number;
    // Drawn instead of the image when the asset is missing (so the user still
    // sees where their logo / headshot will land).
    placeholderLabel?: string;
}

// A single animated shape (StepAnimatedShape). Only numeric x/y/width/height/
// size animate in the presets; colour never tweens, so we keep tweens numeric.
class AnimShape {
    kind: ShapeKind;
    startFrame: number;
    steps: StepSpec[];
    totalFrames: number;
    init: ShapeInit;
    stencils: AnimShape[];

    constructor(
        kind: ShapeKind,
        startSeconds: number,
        init: ShapeInit,
        steps: StepSpec[],
        stencils: AnimShape[] = [],
    ) {
        this.kind = kind;
        this.startFrame = Math.trunc(startSeconds * FPS);
        this.steps = steps;
        this.totalFrames = steps.reduce((acc, s) => acc + Math.trunc(s.duration * FPS), 0);
        this.init = init;
        this.stencils = stencils;
    }

    isVisible(frame: number): boolean {
        return this.startFrame <= frame && frame < this.startFrame + this.totalFrames;
    }

    // Replicates StepAnimatedShape.draw's prop walk: relative `by` tweens resolve
    // against the running value at the moment their step becomes active, so
    // chained tweens accumulate. Pure (no shared mutation across frames).
    propsAt(frame: number): ShapeInit {
        const props: Record<string, number | string | Color | undefined> = { ...this.init };
        const local = frame - this.startFrame;
        let elapsed = 0;
        for (const s of this.steps) {
            const durFrames = Math.trunc(s.duration * FPS);
            if (local < elapsed + durFrames) {
                const t = (local - elapsed) / Math.max(1, durFrames - 1);
                if (s.tweens) {
                    for (const [k, tw] of Object.entries(s.tweens)) {
                        if (Array.isArray(tw.val)) {
                            const s0 = props[k] as Rgba;
                            const e = tw.ease(t);
                            props[k] = [
                                lerp(s0[0], tw.val[0], e),
                                lerp(s0[1], tw.val[1], e),
                                lerp(s0[2], tw.val[2], e),
                                lerp(s0[3], tw.val[3], e),
                            ] as Rgba;
                        } else {
                            const start = props[k] as number;
                            const end = tw.mode === "by" ? start + tw.val : tw.val;
                            props[k] = lerp(start, end, tw.ease(t));
                        }
                    }
                }
                break;
            } else {
                if (s.tweens) {
                    for (const [k, tw] of Object.entries(s.tweens)) {
                        if (Array.isArray(tw.val)) {
                            props[k] = tw.val; // colour tweens are absolute (to)
                        } else {
                            const start = props[k] as number;
                            props[k] = tw.mode === "by" ? start + tw.val : tw.val;
                        }
                    }
                }
                elapsed += durFrames;
            }
        }
        return props as unknown as ShapeInit;
    }
}

// ---------------------------------------------------------------------------
// Colour helpers (hex_to_rgba)
// ---------------------------------------------------------------------------

const colorToCss = (color: Color): string => {
    if (typeof color === "string") return color;
    const [r, g, b, a] = color;
    return `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${a})`;
};

// A `#RRGGBB` hex with an applied alpha → an `rgba()` string (falls back to the
// input for non-6-digit hex).
const hexToRgba = (hex: string, alpha: number): string => {
    const h = hex.replace("#", "");
    if (h.length !== 6) return hex;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// A `#RRGGBB` hex with an applied alpha → a normalised [r,g,b,a] tuple in 0..1
// (mirrors modern.py `_with_alpha`, which feeds Rgba tuples to the shapes).
const withAlpha = (hex: string, alpha: number): Rgba => {
    const h = hex.replace("#", "");
    if (h.length !== 6) return [1, 1, 1, alpha];
    return [
        parseInt(h.slice(0, 2), 16) / 255,
        parseInt(h.slice(2, 4), 16) / 255,
        parseInt(h.slice(4, 6), 16) / 255,
        alpha,
    ];
};

// Relative luminance of a `#RRGGBB` hex (modern.py `_luminance`). Scale is
// irrelevant — it's only ever used to compare two brand colours.
const luminance = (hex: string): number => {
    const h = hex.replace("#", "");
    if (h.length !== 6) return 0;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// A near-black version of a brand colour for the legibility scrim (spotlight.py
// `_darken`): keeps only a faint tint so a saturated brand (e.g. red) darkens the
// photo for legibility instead of dyeing it.
const scrimColor = (hex: string, alpha: number): Rgba => {
    const f = 0.26;
    const h = hex.replace("#", "");
    if (h.length !== 6) return [0, 0, 0, alpha];
    return [
        (parseInt(h.slice(0, 2), 16) / 255) * f,
        (parseInt(h.slice(2, 4), 16) / 255) * f,
        (parseInt(h.slice(4, 6), 16) / 255) * f,
        alpha,
    ];
};

// Trace a rounded-rect path (Rectangle.shapePath with radius). Clamped exactly
// like Cairo: r = min(radius, w/2, h/2).
const roundRectPath = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    radius: number,
): void => {
    const r = Math.min(radius, w / 2, h / 2);
    ctx.beginPath();
    if (r <= 0) {
        ctx.rect(x, y, w, h);
        return;
    }
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
};

// (width, height) of a logo's mark with its uniform background trimmed off, so a
// plate can be sized to hug it (shapes.py `trimmed_logo_box`). Best-effort: the
// preview loads images WITHOUT crossOrigin, so reading pixels back usually taints
// the canvas and throws — we then fall back to the natural size (same aspect, no
// trim), and the caller falls back again to its max box when there is no image.
interface TrimBox {
    x: number;
    y: number;
    w: number;
    h: number;
}
const trimmedLogoBox = (img: HTMLImageElement | undefined): TrimBox | null => {
    if (!img || !img.naturalWidth || !img.naturalHeight) return null;
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    const full: TrimBox = { x: 0, y: 0, w: iw, h: ih };
    try {
        const c = document.createElement("canvas");
        c.width = iw;
        c.height = ih;
        const cx = c.getContext("2d");
        if (!cx) return full;
        cx.drawImage(img, 0, 0);
        const data = cx.getImageData(0, 0, iw, ih).data;
        const corner = [data[0], data[1], data[2], data[3]];
        let minX = iw;
        let minY = ih;
        let maxX = -1;
        let maxY = -1;
        for (let py = 0; py < ih; py++) {
            for (let px = 0; px < iw; px++) {
                const i = (py * iw + px) * 4;
                if (
                    data[i] !== corner[0] ||
                    data[i + 1] !== corner[1] ||
                    data[i + 2] !== corner[2] ||
                    data[i + 3] !== corner[3]
                ) {
                    if (px < minX) minX = px;
                    if (px > maxX) maxX = px;
                    if (py < minY) minY = py;
                    if (py > maxY) maxY = py;
                }
            }
        }
        if (maxX < minX || maxY < minY) return full;
        return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
    } catch {
        // Tainted canvas (cross-origin image) — fall back to the natural box.
        return full;
    }
};

// ---------------------------------------------------------------------------
// Text measurement — a single shared offscreen 2D context, mirroring PIL's
// glyph-bbox measurement closely enough for layout parity.
// ---------------------------------------------------------------------------

let measureCtx: CanvasRenderingContext2D | null = null;
const getMeasureCtx = (): CanvasRenderingContext2D => {
    if (!measureCtx) {
        const c = document.createElement("canvas");
        measureCtx = c.getContext("2d") as CanvasRenderingContext2D;
    }
    return measureCtx;
};

const fontString = (meta: SlideshowFontMeta, size: number): string =>
    `${meta.style === "italic" ? "italic " : ""}${meta.weight} ${size}px ${meta.cssFamily}`;

interface Metrics {
    advance: number; // advance width (~ PIL getlength)
    inkHeight: number; // glyph ink height (~ getbbox height)
    ascent: number; // ink rise above the baseline (~ PIL ascent - getbbox()[1])
}

const measure = (meta: SlideshowFontMeta, size: number, text: string): Metrics => {
    const ctx = getMeasureCtx();
    ctx.font = fontString(meta, size);
    const m = ctx.measureText(text);
    const ascent = m.actualBoundingBoxAscent || 0;
    const descent = m.actualBoundingBoxDescent || 0;
    return { advance: m.width, inkHeight: ascent + descent, ascent };
};

// fitText: largest size that fits text within maxWidth x maxHeight. Uses the
// advance width (measureText().width) — reliable across browsers — rather than
// the glyph ink box, and a full bounded binary search so the result is
// guaranteed to actually fit (a too-tight ±2 search around a wrong estimate
// could return an oversized value that overflows its box).
const fitText = (
    meta: SlideshowFontMeta,
    text: string,
    maxWidth: number,
    maxHeight: number,
): { size: number; width: number; height: number } => {
    const ref = 100;
    const refM = measure(meta, ref, text);
    const refW = refM.advance;
    const refH = refM.inkHeight || ref;
    if (refW === 0) return { size: 1, width: 0, height: 0 };
    const est = Math.max(1, Math.round(ref * Math.min(maxWidth / refW, maxHeight / refH)));
    let low = 1;
    let high = est + 4;
    let best = 1;
    let bestW = 0;
    let bestH = 0;
    while (low <= high) {
        const mid = (low + high) >> 1;
        const mm = measure(meta, mid, text);
        const w = mm.advance;
        const h = mm.inkHeight || mid;
        if (w <= maxWidth && h <= maxHeight) {
            best = mid;
            bestW = w;
            bestH = h;
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }
    return { size: best, width: bestW, height: bestH };
};

// ---------------------------------------------------------------------------
// Caption layout primitives (motion_graphics_preset.py). Captions are sized once
// per VIDEO, not per slide, so every caption in a slideshow shares one header
// size and one subheader size.
// ---------------------------------------------------------------------------

// Split into two length-balanced lines, or [text] if it can't be split
// (split_balanced / magazine._wrap_two).
const splitBalanced = (text: string): string[] => {
    const clean = (text ?? "").trim();
    const words = clean.split(/\s+/).filter(Boolean);
    if (words.length < 2) return [clean];
    let bestI = 1;
    let bestDiff = Infinity;
    for (let i = 1; i < words.length; i++) {
        const diff = Math.abs(
            words.slice(0, i).join(" ").length - words.slice(i).join(" ").length,
        );
        if (diff < bestDiff) {
            bestDiff = diff;
            bestI = i;
        }
    }
    return [words.slice(0, bestI).join(" "), words.slice(bestI).join(" ")];
};

// The largest single font size at which EVERY string fits on one line in
// (width, height) — the min of the per-string best fits, so the whole set shares
// one size and the longest still fits (uniform_font_size).
//
// Taking the min also PROPAGATES fitText's advance-vs-ink-bbox divergence from
// PIL: the single worst-diverging caption sets the size for the entire video,
// where per-slide sizing confined that error to one slide. Still a pixel or two,
// and in the same direction as the renderer's own min.
const uniformFontSize = (
    meta: SlideshowFontMeta,
    strings: string[],
    width: number,
    height: number,
): number => {
    const sizes = strings
        .filter((s) => s && s.trim())
        .map((s) => fitText(meta, s.trim(), width, height).size);
    return sizes.length ? Math.min(...sizes) : Math.trunc(height);
};

// ONE uniform subheader size + per-sub line rows for a whole video (sub_layout).
// Prefers a single line: only when the shared single-line size would fall below
// `minPx` do the too-long subheaders wrap to two balanced lines, with the size
// then taken from the resulting (shorter) lines.
const subLayout = (
    meta: SlideshowFontMeta,
    subs: string[],
    width: number,
    boxH: number,
    lineH: number,
    minPx = SUB_MIN_SINGLE_PX,
): { size: number; rows: string[][] } => {
    const clean = subs.map((s) => (s ?? "").trim());
    const single = uniformFontSize(meta, clean, width, boxH);
    if (single >= minPx) return { size: single, rows: clean.map((s) => [s]) };
    const rows = clean.map((s) =>
        fitText(meta, s || "Mg", width, boxH).size < minPx ? splitBalanced(s) : [s],
    );
    const lines = rows.flat().filter(Boolean);
    return { size: uniformFontSize(meta, lines, width, lineH), rows };
};

// Shrink the subheader until the header is at least `ratio` times its size, but
// never below `floor` (capped_sub_size). A header binds on WIDTH and a subheader
// on HEIGHT, so a wordy caption set renders the subheader LARGER than its own
// header — the title reads as the subtitle. Widening the box can't fix it (the
// same width feeds both), so shrinking the subheader is the only lever. `floor`
// wins on conflict: nothing bounds header length, and legibility is functional
// where the ratio is aesthetic.
const cappedSubSize = (
    subSize: number,
    headerSize: number,
    floor = SUB_MIN_SINGLE_PX,
    ratio = CAPTION_MIN_HEADER_RATIO,
): number => Math.max(Math.min(subSize, Math.trunc(headerSize / ratio)), floor);

// Top y that vertically centres text of `size` in a box (centered_y). Used when
// drawing at an explicit uniform size, which bypasses fitText's own centring.
const centeredY = (
    meta: SlideshowFontMeta,
    boxY: number,
    boxH: number,
    size: number,
    sample = "Ag",
): number => boxY + (boxH - measure(meta, size, sample).inkHeight) / 2;

// Top y at which to paint `text` so its BASELINE lands on `baselineY`
// (baseline_top) — i.e. stack text by the gap BELOW it rather than by centring it
// in a reserved box. A headline nearly always binds on width, so its box is mostly
// empty and centring dumps half that surplus into the seam under it, which is how
// a declared 18px gap renders as 57px. Pinning a fixed ink top instead would shift
// any string that inks higher than the caps it sits with (an accented capital);
// anchoring the baseline keeps the seam exact for any casing.
const baselineTop = (
    meta: SlideshowFontMeta,
    baselineY: number,
    size: number,
    text: string,
): number => baselineY - measure(meta, size, text || " ").ascent;

// ---------------------------------------------------------------------------
// Scene: holds template geometry + builds the shape list for a template, then
// draws a frozen frame (background composite/photo + overlay shapes).
// ---------------------------------------------------------------------------

export interface SceneAssets {
    person?: HTMLImageElement;
    logo?: HTMLImageElement;
    photos: (HTMLImageElement | undefined)[];
}

interface Border {
    l: number;
    t: number;
    r: number;
    b: number;
}

export class PreviewScene {
    width!: number;
    height!: number;
    border!: Border;
    logoDim!: [number, number];
    colors!: { base: string; accent: string; inverse: string };
    font: SlideshowFontMeta;

    introDuration = 3;
    outroDuration = 7;
    remainingDuration: number;
    totalDuration: number;

    cfg: PreviewConfig;
    template: TemplateId;
    private kenBurns = false;
    // Gates every recency element (pill badge / headline band / RECENTLY-LISTED
    // bar) exactly as the compositor does; outros never depend on it.
    private recentlyListed: boolean;
    shapes: AnimShape[] = [];
    // template5 (Modern) sizes its logo plate / watermark to the logo's aspect at
    // build time, so the loaded logo image is passed in up front (mirrors the
    // backend reading logo_path during generate_motion_graphics()).
    private logoImg?: HTMLImageElement;

    constructor(
        template: TemplateId,
        cfg: PreviewConfig,
        hints?: { logo?: HTMLImageElement; person?: HTMLImageElement },
    ) {
        // The Ken Burns templates subclass their static siblings and change only
        // how a slide moves, never the frame, furniture or crop — so the scene is
        // built as the sibling and the motion is applied when the photo is drawn.
        this.kenBurns = template === "template10" || template === "template11";
        // `base` is what the rest of the constructor must branch on — reading the
        // raw parameter below would drop template10/11 into the fallback frame.
        const base: TemplateId =
            template === "template10"
                ? "template7"
                : template === "template11"
                  ? "template9"
                  : template;
        this.template = base;
        this.cfg = cfg;
        this.recentlyListed = showRecencyBadge(cfg);
        this.logoImg = hints?.logo;
        this.font = parseSlideshowFont(cfg.font);
        const c = cfg.colors && cfg.colors.length >= 3 ? cfg.colors : DEFAULT_COLORS;
        this.colors = { base: c[0], accent: c[1], inverse: c[2] };

        if (base === "template4") {
            this.width = 720;
            this.height = 1280;
            this.logoDim = [200, 280];
            this.border = { l: 100, t: 0, r: 100, b: 50 };
        } else if (base === "template9") {
            // Spotlight Badge portrait: photo-led 720x1280 (template_9.py).
            this.width = 720;
            this.height = 1280;
            this.logoDim = [300, 140];
            this.border = { l: 60, t: 60, r: 60, b: 60 };
        } else if (base === "template7") {
            // Spotlight Badge landscape: photo-led 1280x720 (template_7.py).
            this.width = 1280;
            this.height = 720;
            this.logoDim = [360, 140];
            this.border = { l: 80, t: 80, r: 80, b: 80 };
        } else if (base === "template8") {
            // Vertical Editorial portrait: photo-led 720x1280 (template_8.py).
            this.width = 720;
            this.height = 1280;
            this.logoDim = [360, 150];
            this.border = { l: 60, t: 60, r: 60, b: 60 };
        } else if (base === "template5" || base === "template6") {
            this.width = 1280;
            this.height = 720;
            this.logoDim = [360, 140];
            this.border = { l: 80, t: 80, r: 80, b: 80 };
        } else {
            this.width = 1280;
            this.height = 720;
            this.logoDim = [200, 200];
            this.border = { l: 100, t: 100, r: 100, b: 100 };
        }
        const n = Math.max(1, cfg.imageCount);
        this.remainingDuration = n * 4;
        this.totalDuration = this.introDuration + this.remainingDuration + this.outroDuration;
        this.shapes = this.generate();
    }

    // -- shape factories (mirror MotionGraphicsPreset.rectangle/.text) --------

    private rect(
        start: number,
        x: number,
        y: number,
        width: number,
        height: number,
        steps: StepSpec[],
        color: Color = [1, 0, 0, 1],
        stencils: AnimShape[] = [],
        radius = 0,
    ): AnimShape {
        return new AnimShape("rect", start, { x, y, width, height, color, radius }, steps, stencils);
    }

    // Circle: a filled disc centred at (cx, cy) with radius `size` (shapes.py
    // `Circle`). Also used as a stencil for circular image clips.
    private circle(
        start: number,
        cx: number,
        cy: number,
        size: number,
        steps: StepSpec[],
        color: Color = [1, 1, 1, 1],
        stencils: AnimShape[] = [],
    ): AnimShape {
        return new AnimShape("circle", start, { x: cx, y: cy, size, color }, steps, stencils);
    }

    // LinearGradient: a rect filled start→end (vector as fractions of the rect).
    private gradient(opts: {
        start: number;
        x: number;
        y: number;
        width: number;
        height: number;
        color: Color;
        color2: Color;
        steps: StepSpec[];
        x1?: number;
        y1?: number;
        x2?: number;
        y2?: number;
        stencils?: AnimShape[];
    }): AnimShape {
        return new AnimShape(
            "gradient",
            opts.start,
            {
                x: opts.x,
                y: opts.y,
                width: opts.width,
                height: opts.height,
                color: opts.color,
                color2: opts.color2,
                gx1: opts.x1 ?? 0,
                gy1: opts.y1 ?? 0,
                gx2: opts.x2 ?? 1,
                gy2: opts.y2 ?? 0,
            },
            opts.steps,
            opts.stencils ?? [],
        );
    }

    // ImageShape: paints a loaded asset (logo/person) contain/cover/trim-fitted
    // inside its box, with an opacity and optional clip stencils.
    private image(opts: {
        start: number;
        x: number;
        y: number;
        width: number;
        height: number;
        imageRef: "logo" | "person";
        steps: StepSpec[];
        fit?: "contain" | "cover" | "trim";
        opacity?: number;
        stencils?: AnimShape[];
        placeholderLabel?: string;
    }): AnimShape {
        return new AnimShape(
            "image",
            opts.start,
            {
                x: opts.x,
                y: opts.y,
                width: opts.width,
                height: opts.height,
                color: [0, 0, 0, 0],
                imageRef: opts.imageRef,
                fit: opts.fit ?? "contain",
                opacity: opts.opacity ?? 1,
                placeholderLabel: opts.placeholderLabel,
            },
            opts.steps,
            opts.stencils ?? [],
        );
    }

    private text(
        opts: {
            start: number;
            x: number;
            y: number;
            text: string;
            steps: StepSpec[];
            size?: number;
            width?: number;
            height?: number;
            padding?: number;
            verticallyCentered?: boolean;
            color?: Color;
            stencils?: AnimShape[];
            borderWidth?: number;
            borderColor?: Color;
            weight?: number;
        },
    ): AnimShape {
        const padding = opts.padding ?? 0;
        const b = opts.borderWidth ?? 0;
        // Fit and (later) render in the requested role weight so the measured
        // size matches what is drawn (Slideshow.font_for picks a weight per role).
        const meta = opts.weight ? { ...this.font, weight: opts.weight } : this.font;
        let size = opts.size;
        let x = opts.x;
        let y = opts.y;
        if (size === undefined) {
            // Reserve room for the stroke so the outline isn't clipped at the
            // box edges, then inset the text by the border on each side.
            const fit = fitText(
                meta,
                opts.text,
                (opts.width as number) - 2 * padding - 2 * b,
                (opts.height as number) - 2 * padding - 2 * b,
            );
            size = fit.size;
            x += b;
            y += b;
            if (opts.verticallyCentered)
                y += ((opts.height as number) - 2 * b - fit.height) / 2;
        }
        return new AnimShape(
            "text",
            opts.start,
            {
                x: x + padding,
                y,
                text: opts.text,
                size,
                color: opts.color ?? [1, 0, 0, 1],
                borderWidth: opts.borderWidth,
                borderColor: opts.borderColor,
                weight: opts.weight,
            },
            opts.steps,
            opts.stencils ?? [],
        );
    }

    private inkHeight(text: string, size: number): number {
        return measure(this.font, size, text).inkHeight;
    }
    private advance(text: string, size: number): number {
        return measure(this.font, size, text).advance;
    }

    // -- intro + outro (shared by template1/2/3; scroll.py et al.) ------------

    private landscapeIntro(): AnimShape[] {
        const W = this.width;
        const H = this.height;
        const B = this.border;
        const [p0, p1, p2, p3] = PADDING;
        const ld = this.logoDim;
        const { firstname, lastname } = this.cfg;
        const leftText = "RECENTLY";
        const rightText = "LISTED";
        const horizontalPadding = 15;

        const stencils = this.rect(0, B.l, B.t, W / 2 - B.l - p0 - p2, H, [step(7)]);
        const sx = stencils.init.x;
        const sy = stencils.init.y;
        const sw = stencils.init.width as number;
        const sh = stencils.init.height as number;

        const fit = fitText(this.font, leftText + rightText, sw - 2 * p0 - 2 * p2, sh - p1 - p3);
        const size = fit.size;
        const rightTextWidth = this.advance(rightText, size);
        const leftTextWidth = this.advance(leftText, size);

        const Bar = this.rect(
            0,
            sw + sx,
            sy,
            leftTextWidth + p0 + p2,
            this.inkHeight(leftText, size) + p1 + p3,
            [
                step(1, { x: by(-leftTextWidth - p0 - p2, easeInOut) }),
                step(1, { x: to(sx, easeInOut) }),
                step(3),
                step(1, { x: by(sw - leftTextWidth - p0 - p2, easeInOut) }),
                step(1, { x: to(sw + sx, easeInOut) }),
            ],
            this.colors.base,
            [stencils],
        );
        const rightStencil = this.rect(
            0,
            sx + leftTextWidth,
            sy,
            sw - leftTextWidth + p0 + p2,
            sh / 2,
            [step(7)],
        );
        // Translucent panel behind "LISTED" so the base-coloured word stays
        // legible over the photo (no solid bar like "RECENTLY"). Slides with the
        // word, clipped to the same reveal window.
        const rightTextHeight = this.inkHeight(rightText, size);
        const rightTextScrim = this.rect(
            0,
            sx - p0,
            sy + rightTextHeight - p1,
            rightTextWidth + p0 + p2,
            rightTextHeight + p1 + p3,
            [
                step(1),
                step(1, { x: by(leftTextWidth + 2 * p0 + p2, easeInOut) }),
                step(3),
                step(1, { x: by(-leftTextWidth - 2 * p0 - p2, easeInOut) }),
            ],
            hexToRgba(this.colors.inverse, 0.85),
            [rightStencil],
        );
        const rightTextshape = this.text({
            start: 0,
            x: sx,
            y: sy + this.inkHeight(rightText, size),
            text: rightText,
            size,
            color: this.colors.base,
            stencils: [rightStencil],
            steps: [
                step(1),
                step(1, { x: by(leftTextWidth + 2 * p0 + p2, easeInOut) }),
                step(3),
                step(1, { x: by(-leftTextWidth - 2 * p0 - p2, easeInOut) }),
            ],
        });
        const leftTextshape = this.text({
            start: 0,
            x: sx,
            y: sy + this.inkHeight(leftText, size),
            text: leftText,
            size,
            color: this.colors.inverse,
            stencils: [Bar],
            steps: [
                step(1, { x: by(rightTextWidth + 2 * p0 + p2, easeInOut) }),
                step(1, { x: by(-rightTextWidth - p0 - p2, easeInOut) }),
                step(3),
                step(1, { x: by(rightTextWidth + p0 + p2, easeInOut) }),
                step(1, { x: by(-rightTextWidth - 2 * p0 - p2, easeInOut) }),
            ],
        });

        const leftWidthForName = W / 2 - ld[0] - B.l - p0 - p2;
        const firstFit = fitText(this.font, firstname, leftWidthForName, ld[1] / 2);
        const lastFit = fitText(this.font, lastname, leftWidthForName, ld[1] / 2);
        const nameSize = Math.min(firstFit.size, lastFit.size);
        const firstHeight = this.inkHeight(firstname, nameSize);
        const lastHeight = this.inkHeight(lastname, nameSize);
        const nameShift = B.b + ld[1] / 2 + (firstHeight + lastHeight + 2 * horizontalPadding) / 2;

        const firstnameShape = this.text({
            start: 0,
            x: B.l + ld[0] + p0,
            y: H,
            text: firstname,
            size: nameSize,
            color: this.colors.base,
            steps: [
                step(1, { y: by(-nameShift, easeInOut) }),
                step(2),
                step(0.5, { y: by(nameShift, easeInOut) }),
            ],
        });
        const lastnameShape = this.text({
            start: 0,
            x: B.l + ld[0] + p0,
            y: H + firstHeight + 2 * horizontalPadding,
            text: lastname,
            size: nameSize,
            color: this.colors.base,
            steps: [
                step(1, { y: by(-nameShift, easeInOut) }),
                step(2),
                step(0.5, { y: by(nameShift, easeInOut) }),
            ],
        });

        // Painted back-to-front: the bar wipe (when present) sits under the
        // name. With the flag off only the agent name remains (intro.py) — the
        // name is positioned off the logo and border alone, so it doesn't move.
        const phraseShapes = this.recentlyListed
            ? [rightTextScrim, rightTextshape, Bar, leftTextshape]
            : [];
        return [...phraseShapes, firstnameShape, lastnameShape];
    }

    private landscapeOutro(): AnimShape[] {
        const W = this.width;
        const H = this.height;
        const B = this.border;
        const [, , p2, p3] = PADDING;
        const { firstname, lastname, contactNumber } = this.cfg;
        const fullName = `${firstname} ${lastname}`;
        const contact = "CONTACT";
        const start = this.introDuration + this.remainingDuration;

        const leftWidth = W / 2 - B.l - p2;
        const nameFit = fitText(this.font, fullName, leftWidth, H);
        const contactNumFit = fitText(this.font, contactNumber, leftWidth, H);
        const size = Math.min(nameFit.size, contactNumFit.size);

        const cnWidth = this.advance(contactNumber, size);
        const cnHeight = this.inkHeight(contactNumber, size);
        const nameWidth = this.advance(fullName, size);
        const nameHeight = this.inkHeight(fullName, size);

        const cnStencil = this.rect(start, B.l, H - B.b - cnHeight, cnWidth, cnHeight, [step(7)]);
        const cnStencilY = cnStencil.init.y;
        const cnStencilH = cnStencil.init.height as number;
        const contactNumberShape = this.text({
            start,
            x: cnStencil.init.x,
            y: cnStencilY - cnStencilH,
            text: contactNumber,
            width: cnWidth,
            height: cnHeight,
            color: this.colors.base,
            stencils: [cnStencil],
            steps: [
                step(1, { y: by(cnStencilH, easeInOut) }),
                step(3),
                step(1, { y: by(-cnStencilH, easeInOut) }),
            ],
        });

        const nameStencil = this.rect(
            start,
            B.l,
            cnStencilY - p3 - nameHeight,
            nameWidth,
            nameHeight + p3,
            [step(7)],
        );
        const nameStencilH = nameStencil.init.height as number;
        const nameOutroShape = this.text({
            start,
            x: nameStencil.init.x,
            y: nameStencil.init.y + nameStencilH,
            text: fullName,
            width: nameWidth,
            height: nameHeight,
            color: this.colors.base,
            stencils: [nameStencil],
            steps: [
                step(1, { y: by(-nameStencilH, easeInOut) }),
                step(3),
                step(1, { y: by(nameStencilH, easeInOut) }),
            ],
        });

        const contactFit = fitText(this.font, contact, nameWidth / 4, H);
        const contactStencil = this.rect(
            start,
            B.l,
            nameStencil.init.y - p3 - contactFit.height,
            contactFit.width,
            contactFit.height,
            [step(7)],
        );
        const contactStencilH = contactStencil.init.height as number;
        const contactShape = this.text({
            start,
            x: contactStencil.init.x,
            y: contactStencil.init.y - contactStencilH,
            text: contact,
            width: contactFit.width,
            height: contactFit.height,
            color: this.colors.base,
            stencils: [contactStencil],
            steps: [
                step(1, { y: by(contactStencilH, easeInOut) }),
                step(3),
                step(1, { y: by(-contactStencilH, easeInOut) }),
            ],
        });

        return [contactNumberShape, contactShape, nameOutroShape];
    }

    // -- per-image overlays --------------------------------------------------

    private effectiveTexts(): { header: string; subHeader: string }[] {
        const n = Math.max(1, this.cfg.imageCount);
        const out: { header: string; subHeader: string }[] = [];
        for (let i = 0; i < n; i++) {
            const t = this.cfg.texts[i];
            out.push({
                header: t?.header?.trim() || "Your Headline",
                subHeader: t?.subHeader?.trim() || "Sub-headline",
            });
        }
        return out;
    }

    private scrollAnimation(): AnimShape[] {
        const W = this.width;
        const H = this.height;
        const texts = this.effectiveTexts();
        const segmentDuration = this.remainingDuration / texts.length;
        const slideTime = Math.min(1, segmentDuration * 0.2);
        const holdTime = Math.max(0, segmentDuration - 2 * slideTime);
        const out: AnimShape[] = [];
        let offset = this.introDuration;

        texts.forEach((element, i) => {
            const x = i % 2 === 0 ? 150 : W - 150 - 420;
            const stencil = this.rect(offset, x, H - 150, 420, 60, [step(segmentDuration)], [1, 1, 1, 1]);
            const sw = stencil.init.width as number;
            const sh = stencil.init.height as number;
            const sx = stencil.init.x;
            const sy = stencil.init.y;

            const bar = this.rect(
                offset,
                sx - 400 - 20,
                sy,
                400,
                60,
                [
                    step(slideTime, { x: by(sw, easeInOut) }),
                    step(holdTime),
                    step(slideTime, { x: by(-sw, easeInOut) }),
                ],
                this.colors.base,
                [stencil],
            );
            const tip = this.rect(
                offset,
                sx - 20,
                sy,
                20,
                60,
                [
                    step(slideTime, { x: by(sw, easeInOut) }),
                    step(holdTime),
                    step(slideTime, { x: by(-sw, easeInOut) }),
                ],
                this.colors.accent,
                [stencil],
            );
            const header = this.text({
                start: offset,
                x: sx + sw,
                y: sy,
                text: element.header,
                width: bar.init.width as number,
                height: bar.init.height as number,
                padding: 10,
                verticallyCentered: true,
                color: this.colors.inverse,
                stencils: [bar],
                steps: [
                    step(slideTime, { x: by(-sw, easeInOut) }),
                    step(holdTime),
                    step(slideTime, { x: by(sw, easeInOut) }),
                ],
            });
            const subBarStencil = this.rect(
                offset,
                sx + sw - 270,
                H - 150 + sh + 20,
                250,
                40,
                [step(segmentDuration)],
                [1, 0.5, 1, 1],
            );
            const subBarSw = subBarStencil.init.width as number;
            const subBar = this.rect(
                offset,
                subBarStencil.init.x + 250,
                subBarStencil.init.y,
                250,
                40,
                [
                    step(slideTime, { x: by(-subBarSw, easeInOut) }),
                    step(holdTime),
                    step(slideTime, { x: by(subBarSw, easeInOut) }),
                ],
                this.colors.base,
                [subBarStencil],
            );
            const subTipStencil = this.rect(
                offset,
                subBarStencil.init.x + subBarSw,
                H - 150 + sh + 20,
                20,
                40,
                [step(segmentDuration)],
                [1, 1, 0.5, 1],
            );
            const subTipSw = subTipStencil.init.width as number;
            const subTip = this.rect(
                offset,
                subTipStencil.init.x - 20,
                subTipStencil.init.y,
                20,
                40,
                [
                    step(slideTime, { x: by(subTipSw, easeInOut) }),
                    step(holdTime),
                    step(slideTime, { x: by(-subTipSw, easeInOut) }),
                ],
                this.colors.accent,
                [subTipStencil],
            );
            const subHeader = this.text({
                start: offset,
                x: subBarStencil.init.x - (subBar.init.width as number),
                y: subBar.init.y,
                text: element.subHeader,
                width: subBar.init.width as number,
                height: subBar.init.height as number,
                padding: 10,
                verticallyCentered: true,
                color: this.colors.inverse,
                stencils: [subBar],
                steps: [
                    step(slideTime, { x: by(subBar.init.width as number, easeInOut) }),
                    step(holdTime),
                    step(slideTime, { x: by(-(subBar.init.width as number), easeInOut) }),
                ],
            });

            offset += segmentDuration;
            out.push(bar, subBar, header, subHeader, tip, subTip);
        });
        return out;
    }

    private slideInAnimation(): AnimShape[] {
        const W = this.width;
        const H = this.height;
        const texts = this.effectiveTexts();
        const segmentDuration = this.remainingDuration / texts.length;
        const slideTime = Math.min(1, segmentDuration * 0.2);
        const holdTime = Math.max(0, segmentDuration - 2 * slideTime);
        const out: AnimShape[] = [];
        let offset = this.introDuration;

        texts.forEach((element, i) => {
            const x = i % 2 === 0 ? 100 : W - 100 - 960;
            const stencil = this.rect(offset, x, H - 200, 960, 150, [step(segmentDuration)], [1, 0, 0, 1]);
            const sw = stencil.init.width as number;
            const sh = stencil.init.height as number;
            const sx = stencil.init.x;
            const sy = stencil.init.y;

            const header = this.text({
                start: offset,
                x: sx,
                y: sy + sh,
                text: element.header,
                width: sw,
                height: 100,
                color: this.colors.inverse,
                // Dark outline so the light text reads over bright photos
                // (this template has no bar behind the text).
                borderWidth: 3,
                borderColor: this.colors.base,
                stencils: [stencil],
                steps: [
                    step(slideTime, { y: by(-100, easeInOut) }),
                    step(holdTime),
                    step(slideTime, { y: by(100, easeInOut) }),
                ],
            });

            const subFit = fitText(this.font, element.subHeader, sw / 2, 50);
            const shift = i % 2 === 0 ? subFit.width : sw;
            const subHeader = this.text({
                start: offset,
                x: sx - subFit.width,
                // Nudge down by the border so the outline clears the stencil top.
                y: sy + 2,
                text: element.subHeader,
                size: subFit.size,
                color: this.colors.inverse,
                borderWidth: 2,
                borderColor: this.colors.base,
                stencils: [stencil],
                steps: [
                    step(slideTime, { x: by(shift, easeInOut) }),
                    step(holdTime),
                    step(slideTime, { x: by(-shift, easeInOut) }),
                ],
            });

            offset += segmentDuration;
            out.push(header, subHeader);
        });
        return out;
    }

    private scrollDualToneAnimation(): AnimShape[] {
        const W = this.width;
        const H = this.height;
        const texts = this.effectiveTexts();
        const segmentDuration = this.remainingDuration / texts.length;
        const slideTime = Math.min(1, segmentDuration * 0.2);
        const holdTime = Math.max(0, segmentDuration - 2 * slideTime);
        const out: AnimShape[] = [];
        let offset = this.introDuration;

        texts.forEach((element, i) => {
            const x = i % 2 === 0 ? 150 : W - 150 - 420;
            const stencil = this.rect(offset, x, H - 150, 420, 60, [step(segmentDuration)], [1, 1, 1, 1]);
            const sw = stencil.init.width as number;
            const sh = stencil.init.height as number;
            const sx = stencil.init.x;
            const sy = stencil.init.y;

            const backgroundBar = this.rect(
                offset,
                sx - sw,
                sy,
                sw,
                sh,
                [
                    step(slideTime, { x: by(sw, easeInOut) }),
                    step(holdTime),
                    step(slideTime, { x: by(-sw, easeInOut) }),
                ],
                this.colors.base,
                [stencil],
            );
            const foregroundBar = this.rect(
                offset,
                sx - sw,
                sy,
                sw,
                sh,
                [
                    step(slideTime, { x: by(sw * 2, easeInOut) }),
                    step(holdTime),
                    step(slideTime, { x: by(-sw * 2, easeInOut) }),
                ],
                this.colors.inverse,
                [stencil],
            );
            const backgroundHeader = this.text({
                start: offset,
                x: sx + sw,
                y: sy,
                text: element.header,
                width: backgroundBar.init.width as number,
                height: backgroundBar.init.height as number,
                padding: 10,
                verticallyCentered: true,
                color: this.colors.inverse,
                stencils: [backgroundBar],
                steps: [
                    step(slideTime, { x: by(-sw, easeInOut) }),
                    step(holdTime),
                    step(slideTime, { x: by(sw, easeInOut) }),
                ],
            });
            const foregroundHeader = this.text({
                start: offset,
                x: sx + sw,
                y: sy,
                text: element.header,
                width: foregroundBar.init.width as number,
                height: foregroundBar.init.height as number,
                padding: 10,
                verticallyCentered: true,
                color: this.colors.base,
                stencils: [foregroundBar, stencil],
                steps: [
                    step(slideTime, { x: by(-sw, easeInOut) }),
                    step(holdTime),
                    step(slideTime, { x: by(sw, easeInOut) }),
                ],
            });

            offset += segmentDuration;
            out.push(backgroundBar, foregroundBar, backgroundHeader, foregroundHeader);
        });
        return out;
    }

    // -- portrait (template4: scroll_portrait.py) ----------------------------

    private portraitIntro(): AnimShape[] {
        const W = this.width;
        const H = this.height;
        const B = this.border;
        const [p0, p1, p2, p3] = PADDING;
        const ld = this.logoDim;
        const { firstname, lastname } = this.cfg;
        const leftText = "RECENTLY";
        const rightText = "LISTED";

        const stencils = this.rect(0, B.l, ld[1] + B.t + p1, W - B.l, H, [step(7)]);
        const sx = stencils.init.x;
        const sy = stencils.init.y;
        const sw = stencils.init.width as number;
        const sh = stencils.init.height as number;

        const fit = fitText(this.font, leftText + rightText, sw - 2 * p0 - 2 * p2, sh - p1 - p3);
        const size = fit.size;
        const h = fit.height;
        const rightTextWidth = this.advance(rightText, size);
        const leftTextWidth = this.advance(leftText, size);

        const Bar = this.rect(
            0,
            sw + sx,
            sy,
            leftTextWidth + p0 + p2,
            h + p1 + p3,
            [
                step(1, { x: by(-leftTextWidth - p0 - p2, easeInOut) }),
                step(1, { x: to(sx, easeInOut) }),
                step(3),
                step(1, { x: by(sw - leftTextWidth - p0 - p2, easeInOut) }),
                step(1, { x: to(sw + sx, easeInOut) }),
            ],
            this.colors.base,
            [stencils],
        );
        const rightStencil = this.rect(0, sx + leftTextWidth, sy, sw - leftTextWidth + p0 + p2, sh / 2, [step(7)]);
        // Translucent panel behind "LISTED" so the base-coloured word stays
        // legible over the photo (mirrors scroll_portrait.py).
        const rightTextHeight = this.inkHeight(rightText, size);
        const rightTextScrim = this.rect(
            0,
            sx - p0,
            sy,
            rightTextWidth + p0 + p2,
            rightTextHeight + p1 + p3,
            [
                step(1),
                step(1, { x: by(leftTextWidth + 2 * p0 + p2, easeInOut) }),
                step(3),
                step(1, { x: by(-leftTextWidth - 2 * p0 - p2, easeInOut) }),
            ],
            hexToRgba(this.colors.inverse, 0.85),
            [rightStencil],
        );
        const rightTextshape = this.text({
            start: 0,
            x: sx,
            y: sy + p1,
            text: rightText,
            size,
            color: this.colors.base,
            stencils: [rightStencil],
            steps: [
                step(1),
                step(1, { x: by(leftTextWidth + 2 * p0 + p2, easeInOut) }),
                step(3),
                step(1, { x: by(-leftTextWidth - 2 * p0 - p2, easeInOut) }),
            ],
        });
        const leftTextshape = this.text({
            start: 0,
            x: sx,
            y: sy + p1,
            text: leftText,
            size,
            color: this.colors.inverse,
            stencils: [Bar],
            steps: [
                step(1, { x: by(rightTextWidth + 2 * p0 + p2, easeInOut) }),
                step(1, { x: by(-rightTextWidth - p0 - p2, easeInOut) }),
                step(3),
                step(1, { x: by(rightTextWidth + p0 + p2, easeInOut) }),
                step(1, { x: by(-rightTextWidth - 2 * p0 - p2, easeInOut) }),
            ],
        });

        const widthForName = W - B.l - B.r;
        const nameFit = fitText(this.font, `${firstname} ${lastname}`, widthForName, H / 4);
        const nameSize = nameFit.size;
        const firstWidth = this.advance(firstname, nameSize);
        const firstHeight = this.inkHeight(firstname, nameSize);

        // First/last fly in from opposite edges but converge into a single
        // centred "First Last" (mirrors scroll_portrait.py).
        const fullNameWidth = this.advance(`${firstname} ${lastname}`, nameSize);
        const firstRestX = (W - fullNameWidth) / 2;
        const lastRestX = firstRestX + this.advance(`${firstname} `, nameSize);

        const firstnameShape = this.text({
            start: 0,
            x: -firstWidth,
            y: H / 2 - firstHeight - p1,
            text: firstname,
            size: nameSize,
            color: this.colors.base,
            steps: [
                step(1, { x: by(firstRestX + firstWidth, easeInOut) }),
                step(2),
                step(0.5, { x: by(-(firstRestX + firstWidth), easeInOut) }),
            ],
        });
        const lastnameShape = this.text({
            start: 0,
            x: W,
            y: H / 2 - firstHeight - p1,
            text: lastname,
            size: nameSize,
            color: this.colors.base,
            steps: [
                step(1, { x: by(lastRestX - W, easeInOut) }),
                step(2),
                step(0.5, { x: by(W - lastRestX, easeInOut) }),
            ],
        });

        // Painted back-to-front: the bar wipe (when present) sits under the
        // name (scroll_portrait.py).
        const phraseShapes = this.recentlyListed
            ? [rightTextScrim, rightTextshape, Bar, leftTextshape]
            : [];
        return [...phraseShapes, firstnameShape, lastnameShape];
    }

    private portraitOutro(): AnimShape[] {
        const W = this.width;
        const H = this.height;
        const B = this.border;
        const [, , , p3] = PADDING;
        const { firstname, lastname, contactNumber } = this.cfg;
        const fullName = `${firstname} ${lastname}`;
        const contact = "CONTACT";
        const start = this.introDuration + this.remainingDuration;

        const leftWidth = W - B.l - B.r;
        const nameFit = fitText(this.font, fullName, leftWidth, H);
        const contactNumFit = fitText(this.font, contactNumber, leftWidth, H);
        const size = Math.min(nameFit.size, contactNumFit.size);

        const cnWidth = this.advance(contactNumber, size);
        const cnHeight = this.inkHeight(contactNumber, size);
        const nameWidth = this.advance(fullName, size);
        const nameHeight = this.inkHeight(fullName, size);

        // CONTACT label size up front so the whole block can be centred in the
        // gap between the logo (top) and the headshot (bottom half) — mirrors
        // scroll_portrait.py so CONTACT never collides with the logo.
        const contactFit = fitText(this.font, contact, nameWidth / 4, H);
        const logoBottom = this.logoDim[1] + B.t;
        const blockHeight = contactFit.height + p3 + nameHeight + p3 + cnHeight;
        const blockTop = logoBottom + (H / 2 - logoBottom - blockHeight) / 2;

        const cnStencil = this.rect(
            start,
            W / 2 - cnWidth / 2,
            blockTop + contactFit.height + p3 + nameHeight + p3,
            cnWidth,
            cnHeight,
            [step(7)],
        );
        const cnStencilH = cnStencil.init.height as number;
        const contactNumberShape = this.text({
            start,
            x: cnStencil.init.x,
            y: cnStencil.init.y - cnStencilH,
            text: contactNumber,
            width: cnWidth,
            height: cnHeight,
            color: this.colors.base,
            stencils: [cnStencil],
            steps: [
                step(1, { y: by(cnStencilH, easeInOut) }),
                step(3),
                step(1, { y: by(-cnStencilH, easeInOut) }),
            ],
        });

        const nameStencil = this.rect(
            start,
            W / 2 - nameWidth / 2,
            cnStencil.init.y - p3 - nameHeight,
            nameWidth,
            nameHeight + p3,
            [step(7)],
        );
        const nameStencilH = nameStencil.init.height as number;
        const nameOutroShape = this.text({
            start,
            x: nameStencil.init.x,
            y: nameStencil.init.y + nameStencilH,
            text: fullName,
            width: nameWidth,
            height: nameHeight,
            color: this.colors.base,
            stencils: [nameStencil],
            steps: [
                step(1, { y: by(-nameStencilH, easeInOut) }),
                step(3),
                step(1, { y: by(nameStencilH, easeInOut) }),
            ],
        });

        const contactStencil = this.rect(
            start,
            W / 2 - contactFit.width / 2,
            nameStencil.init.y - p3 - contactFit.height,
            contactFit.width,
            contactFit.height,
            [step(7)],
        );
        const contactStencilH = contactStencil.init.height as number;
        const contactShape = this.text({
            start,
            x: contactStencil.init.x,
            y: contactStencil.init.y - contactStencilH,
            text: contact,
            width: contactFit.width,
            height: contactFit.height,
            color: this.colors.base,
            stencils: [contactStencil],
            steps: [
                step(1, { y: by(contactStencilH, easeInOut) }),
                step(3),
                step(1, { y: by(-contactStencilH, easeInOut) }),
            ],
        });

        return [contactNumberShape, contactShape, nameOutroShape];
    }

    private portraitAnimation(): AnimShape[] {
        const W = this.width;
        const H = this.height;
        const B = this.border;
        const [, p1] = PADDING;
        const texts = this.effectiveTexts();
        const segmentDuration = this.remainingDuration / texts.length;
        const slideTime = Math.min(1, segmentDuration * 0.2);
        const holdTime = Math.max(0, segmentDuration - 2 * slideTime);
        const out: AnimShape[] = [];
        let offset = this.introDuration;

        texts.forEach((element) => {
            const x = B.l;
            const stencil = this.rect(
                offset,
                x,
                H - B.b - 200,
                W - B.l - B.r,
                100,
                [step(segmentDuration)],
                [1, 1, 1, 1],
            );
            const sw = stencil.init.width as number;
            const sh = stencil.init.height as number;
            const sx = stencil.init.x;
            const sy = stencil.init.y;

            const bar = this.rect(
                offset,
                sx - sw,
                sy,
                sw - 40,
                sh,
                [
                    step(slideTime, { x: by(sw, easeInOut) }),
                    step(holdTime),
                    step(slideTime, { x: by(-sw, easeInOut) }),
                ],
                this.colors.base,
                [stencil],
            );
            const tip = this.rect(
                offset,
                sx - 40,
                sy,
                40,
                sh,
                [
                    step(slideTime, { x: by(sw, easeInOut) }),
                    step(holdTime),
                    step(slideTime, { x: by(-sw, easeInOut) }),
                ],
                this.colors.accent,
                [stencil],
            );
            const header = this.text({
                start: offset,
                x: sx + sw,
                y: sy,
                text: element.header,
                width: bar.init.width as number,
                height: bar.init.height as number,
                padding: 10,
                verticallyCentered: true,
                color: this.colors.inverse,
                stencils: [bar],
                steps: [
                    step(slideTime, { x: by(-sw, easeInOut) }),
                    step(holdTime),
                    step(slideTime, { x: by(sw, easeInOut) }),
                ],
            });

            const barWidth = bar.init.width as number;
            const subBarStencil = this.rect(
                offset,
                sx + barWidth / 3,
                sh + sy + p1,
                (barWidth * 2) / 3,
                70,
                [step(segmentDuration)],
                [1, 0.5, 1, 1],
            );
            const subBarSw = subBarStencil.init.width as number;
            const subBar = this.rect(
                offset,
                subBarStencil.init.x + subBarSw,
                subBarStencil.init.y,
                subBarSw,
                subBarStencil.init.height as number,
                [
                    step(slideTime, { x: by(-subBarSw, easeInOut) }),
                    step(holdTime),
                    step(slideTime, { x: by(subBarSw, easeInOut) }),
                ],
                this.colors.base,
                [subBarStencil],
            );
            const subTipStencil = this.rect(
                offset,
                subBarStencil.init.x + subBarSw,
                subBarStencil.init.y,
                40,
                subBarStencil.init.height as number,
                [step(segmentDuration)],
                [1, 1, 0.5, 1],
            );
            const subTipSw = subTipStencil.init.width as number;
            const subTip = this.rect(
                offset,
                subTipStencil.init.x - subTipSw,
                subTipStencil.init.y,
                subTipSw,
                subTipStencil.init.height as number,
                [
                    step(slideTime, { x: by(subTipSw, easeInOut) }),
                    step(holdTime),
                    step(slideTime, { x: by(-subTipSw, easeInOut) }),
                ],
                this.colors.accent,
                [subTipStencil],
            );
            const subHeader = this.text({
                start: offset,
                x: subBarStencil.init.x - subBarSw,
                y: subBar.init.y,
                text: element.subHeader,
                width: subBarSw,
                height: subBarStencil.init.height as number,
                padding: 10,
                verticallyCentered: true,
                color: this.colors.inverse,
                stencils: [subBar],
                steps: [
                    step(slideTime, { x: by(subBarSw, easeInOut) }),
                    step(holdTime),
                    step(slideTime, { x: by(-subBarSw, easeInOut) }),
                ],
            });

            offset += segmentDuration;
            out.push(bar, subBar, header, subHeader, tip, subTip);
        });
        return out;
    }

    // -- Modern editorial (template5: presets/modern.py) ---------------------

    // ink = the darker brand colour, pop = the brighter — assigned by luminance
    // (not palette slot) so scrims stay dark/readable and the accent stays bright
    // for ANY brand order (modern.py `_ink_pop`).
    private inkPop(): [string, string] {
        const { base, accent } = this.colors;
        return luminance(base) <= luminance(accent) ? [base, accent] : [accent, base];
    }

    // A dark legibility scrim over the left of the photo: a near-solid ink panel
    // that fades to transparent so the right (and the headshot) stay visible.
    private modernScrim(start: number, duration: number, ink: string): AnimShape[] {
        const solidW = this.width * 0.56;
        const panel = this.rect(start, 0, 0, solidW, this.height, [step(duration)], withAlpha(ink, 0.84));
        const fade = this.gradient({
            start,
            x: solidW,
            y: 0,
            width: this.width * 0.26,
            height: this.height,
            color: withAlpha(ink, 0.84),
            color2: withAlpha(ink, 0),
            x1: 0,
            y1: 0,
            x2: 1,
            y2: 0,
            steps: [step(duration)],
        });
        return [panel, fade];
    }

    // White rounded plate sized to hug the logo's trimmed mark, with the logo on
    // top (modern.py `_logo_chip`). Plate fits within (maxW, maxH).
    private modernLogoChip(
        start: number,
        duration: number,
        x: number,
        y: number,
        maxW: number,
        maxH: number,
        pad = 16,
        steps?: StepSpec[],
    ): AnimShape[] {
        const box = trimmedLogoBox(this.logoImg) ?? { x: 0, y: 0, w: maxW, h: maxH };
        const fit = Math.min(maxW / box.w, maxH / box.h);
        const drawW = box.w * fit;
        const drawH = box.h * fit;
        const plate = this.rect(
            start,
            x,
            y,
            drawW + 2 * pad,
            drawH + 2 * pad,
            steps ?? [step(duration)],
            "#ffffff",
            [],
            12,
        );
        const logo = this.image({
            start,
            x: x + pad,
            y: y + pad,
            width: drawW,
            height: drawH,
            imageRef: "logo",
            fit: "trim",
            steps: steps ?? [step(duration)],
            placeholderLabel: "Logo",
        });
        return [plate, logo];
    }

    private modernLogoChipSize(maxW: number, maxH: number, pad = 16): { w: number; h: number } {
        const box = trimmedLogoBox(this.logoImg) ?? { x: 0, y: 0, w: maxW, h: maxH };
        const fit = Math.min(maxW / box.w, maxH / box.h);
        return { w: box.w * fit + 2 * pad, h: box.h * fit + 2 * pad };
    }

    // Framed headshot: a near-white rounded card with the cover-fitted headshot
    // clipped to a slightly inset rounded rect (modern.py `_headshot_card`).
    private modernHeadshotCard(
        start: number,
        duration: number,
        x: number,
        y: number,
        w: number,
        h: number,
    ): AnimShape[] {
        const border = 8;
        const card = this.rect(start, x, y, w, h, [step(duration)], this.colors.inverse, [], 16);
        const inner = this.rect(
            start,
            x + border,
            y + border,
            w - 2 * border,
            h - 2 * border,
            [step(duration)],
            [1, 1, 1, 1],
            [],
            12,
        );
        const shot = this.image({
            start,
            x: x + border,
            y: y + border,
            width: w - 2 * border,
            height: h - 2 * border,
            imageRef: "person",
            fit: "cover",
            stencils: [inner],
            steps: [step(duration)],
            placeholderLabel: "Headshot",
        });
        return [card, shot];
    }

    // Shared left-hand content of the photo intro/outro: scrim, logo chip, a
    // masked headline, a pop accent rule, the headshot card, and a stack of
    // light/pop text lines (modern.py `_editorial_panel`).
    //
    // `headline` may be null (the intro with recently_listed off): the band is
    // then collapsed rather than left empty — the rule and the lines that derive
    // from it slide up by the band's full height so the block still hugs the
    // logo chip.
    private modernEditorialPanel(
        start: number,
        duration: number,
        headline: string | null,
        subLines: [string, Color][],
    ): AnimShape[] {
        const [ink] = this.inkPop();
        const pop = this.accentOnDark();
        const inverse = this.colors.inverse;
        const headMeta = { ...this.font, weight: MODERN_HEADLINE_WEIGHT };
        const bx = 80;

        let shapes = this.modernScrim(start, duration, ink);
        shapes = shapes.concat(this.modernLogoChip(start, duration, bx, 60, 320, 120));

        const headY = 290;
        const headH = 120;
        const headW = this.width * 0.5;
        if (headline) {
            // One size for both bookends: fit the longest title, then render this
            // title at that fixed size (vertically centred) so intro & closing match.
            const commonSize = Math.min(
                ...MODERN_BOOKENDS.map((t) => fitText(headMeta, t, headW, headH).size),
            );
            const hm = measure(headMeta, commonSize, headline);
            const headTextY = headY + (headH - hm.inkHeight) / 2;
            const reveal = this.rect(
                start + 0.3,
                bx,
                headY,
                0,
                headH,
                [step(0.7, { width: to(hm.advance + 14, easeInOut) }), step(duration - 1.0)],
                [1, 1, 1, 1],
            );
            shapes.push(
                this.text({
                    start: start + 0.3,
                    x: bx,
                    y: headTextY,
                    text: headline,
                    size: commonSize,
                    color: inverse,
                    weight: MODERN_HEADLINE_WEIGHT,
                    stencils: [reveal],
                    steps: [step(duration - 0.3)],
                }),
            );
        }

        const ruleY = headline ? headY + headH + 16 : headY;
        shapes.push(
            this.rect(
                start + 0.9,
                bx,
                ruleY,
                0,
                6,
                [step(0.5, { width: to(220, easeInOut) }), step(duration - 1.4)],
                pop,
            ),
        );

        let lineY = ruleY + 26;
        subLines.forEach(([text, color], i) => {
            const lineH = 46;
            const lineWindow = this.rect(
                start + 1.0 + 0.15 * i,
                bx,
                lineY,
                headW,
                lineH,
                [step(duration - 1.0 - 0.15 * i)],
                [1, 1, 1, 1],
            );
            shapes.push(
                this.text({
                    start: start + 1.0 + 0.15 * i,
                    x: bx,
                    y: lineY + lineH,
                    text,
                    width: headW,
                    height: lineH,
                    verticallyCentered: true,
                    color,
                    weight: MODERN_BODY_WEIGHT,
                    stencils: [lineWindow],
                    steps: [
                        step(0.6, { y: by(-lineH, easeInOut) }),
                        step(duration - 1.6 - 0.15 * i),
                    ],
                }),
            );
            lineY += lineH + 12;
        });

        const cardW = 330;
        const cardH = 540;
        const cardX = this.width - 70 - cardW;
        const cardY = (this.height - cardH) / 2;
        shapes = shapes.concat(this.modernHeadshotCard(start, duration, cardX, cardY, cardW, cardH));

        return shapes;
    }

    private modernIntro(): AnimShape[] {
        const fullName = `${this.cfg.firstname} ${this.cfg.lastname}`.trim();
        return this.modernEditorialPanel(
            0,
            this.introDuration,
            this.recentlyListed ? MODERN_INTRO_HEADLINE : null,
            [[fullName, this.colors.inverse]],
        );
    }

    private modernOutro(): AnimShape[] {
        const pop = this.accentOnDark();
        const fullName = `${this.cfg.firstname} ${this.cfg.lastname}`.trim();
        const start = this.introDuration + this.remainingDuration;
        return this.modernEditorialPanel(start, this.outroDuration, MODERN_OUTRO_HEADLINE, [
            [fullName, this.colors.inverse],
            [this.cfg.contactNumber, pop],
        ]);
    }

    private modernCaption(): AnimShape[] {
        const texts = this.effectiveTexts();
        if (!texts.length) return [];
        const [ink] = this.inkPop();
        const pop = this.accentOnDark();
        const inverse = this.colors.inverse;
        const cardColor = withAlpha(ink, 0.8);

        const introDuration = this.introDuration;
        const remaining = this.remainingDuration;
        const segment = remaining / texts.length;
        const slideTime = Math.min(0.6, segment * 0.2);
        const hold = Math.max(0, segment - 2 * slideTime);

        const cardX = this.border.l;
        const cardW = 600;
        const cardH = 120;
        const cardY = this.height - cardH - this.border.b;
        const cardRadius = 14;
        const tipW = 10;
        const inset = 28;
        const headGap = 18;
        // Start fully clear of the stencil window so the card slides in from the left.
        const delta = cardW + 60;

        const slideSteps = (): StepSpec[] => [
            step(slideTime, { x: by(delta, easeInOut) }),
            step(hold),
            step(slideTime, { x: by(-delta, easeInOut) }),
        ];

        // Persistent logo watermark (bottom-right) over every photo, on a white
        // backing that hugs the logo's own aspect ratio (modern.py watermark).
        const wmMaxW = 170;
        const wmMaxH = 64;
        const wmPad = 12;
        const box = trimmedLogoBox(this.logoImg) ?? { x: 0, y: 0, w: wmMaxW, h: wmMaxH };
        const wmFit = Math.min(wmMaxW / box.w, wmMaxH / box.h);
        const drawW = box.w * wmFit;
        const drawH = box.h * wmFit;
        const backW = drawW + 2 * wmPad;
        const backH = drawH + 2 * wmPad;
        const backX = this.width - this.border.r - backW;
        const backY = this.height - this.border.b - backH;
        const wmBacking = this.rect(
            introDuration,
            backX,
            backY,
            backW,
            backH,
            [step(remaining)],
            "#ffffff",
            [],
            10,
        );
        const watermark = this.image({
            start: introDuration,
            x: backX + wmPad,
            y: backY + wmPad,
            width: drawW,
            height: drawH,
            imageRef: "logo",
            fit: "trim",
            steps: [step(remaining)],
            placeholderLabel: "Logo",
        });

        // One uniform header size and one uniform subheader size for the whole
        // video; subheaders stay single-line where they fit (see subLayout).
        const headMeta = { ...this.font, weight: MODERN_CAPTION_HEADER_WEIGHT };
        const subMeta = { ...this.font, weight: MODERN_CAPTION_SUB_WEIGHT };
        const subX = cardX + inset - delta;
        const subW = cardW - inset - 24;
        const headerSize = uniformFontSize(headMeta, texts.map((t) => t.header), subW, 50);
        const sub = subLayout(subMeta, texts.map((t) => t.subHeader), subW, 30, 24);
        // A width-crushed header would otherwise render SMALLER than its own
        // subheader. Must precede subPitch/subInk so the block is measured at the
        // final size.
        const subSize = cappedSubSize(sub.size, headerSize);
        const subPitch = subSize * 1.18;
        // The header sat at a fixed cardY + 18 with the subheader at a fixed
        // cardY + 78, so the gap between them was (78 - 18) - headerInk — it GREW as
        // the captions got longer. Measure the block and centre it in the card.
        // headRise is the tallest ink ABOVE the baseline of any header in the video,
        // not a cap-height sample: a round capital overshoots the cap line slightly
        // and an accent by far more, either of which would push the block past the
        // card. The video-wide max keeps the block identical on every slide.
        const headRise = Math.max(
            ...texts.map((t) => measure(headMeta, headerSize, t.header || " ").ascent),
        );
        const subInk = measure(subMeta, subSize, "Ag").inkHeight;

        const animation: AnimShape[] = [];
        let offset = introDuration;
        texts.forEach((element, i) => {
            const cardWindow = this.rect(offset, cardX, cardY, cardW, cardH, [step(segment)], [1, 1, 1, 1]);
            const card = this.rect(
                offset,
                cardX - delta,
                cardY,
                cardW,
                cardH,
                slideSteps(),
                cardColor,
                [cardWindow],
                cardRadius,
            );
            // Inset within the rounded corners so the tab doesn't poke past the curve.
            const tip = this.rect(
                offset,
                cardX - delta,
                cardY + cardRadius,
                tipW,
                cardH - 2 * cardRadius,
                slideSteps(),
                pop,
                [cardWindow],
            );
            const subRows = sub.rows[i];
            const subBlock = (subRows.length - 1) * subPitch + subInk;
            // The caption is stencilled to the card, so anything past its bottom
            // edge is silently CLIPPED. A two-line subheader needs more than this
            // card is tall, so give back gap rather than cut a line off.
            const gap = Math.min(headGap, Math.max(0, cardH - headRise - subBlock));
            const top = cardY + (cardH - (headRise + gap + subBlock)) / 2;
            const header = this.text({
                start: offset,
                x: subX,
                y: baselineTop(headMeta, top + headRise, headerSize, element.header),
                text: element.header,
                size: headerSize,
                color: inverse,
                weight: MODERN_CAPTION_HEADER_WEIGHT,
                stencils: [cardWindow],
                steps: slideSteps(),
            });
            const subTop = top + headRise + gap;
            const subHeaders = subRows.map((ln, j) =>
                this.text({
                    start: offset,
                    x: subX,
                    y: subTop + j * subPitch,
                    text: ln,
                    size: subSize,
                    color: inverse,
                    weight: MODERN_CAPTION_SUB_WEIGHT,
                    stencils: [cardWindow],
                    steps: slideSteps(),
                }),
            );

            animation.push(card, tip, header, ...subHeaders);
            offset += segment;
        });

        animation.push(wmBacking, watermark);
        return animation;
    }

    // -- Split magazine (template6: presets/magazine.py) ---------------------

    // [panelX, panelW]: the photo fills the left, the brand panel the right.
    private panelGeom(): [number, number] {
        const panelX = this.width * MAGAZINE_PANEL_FRAC;
        return [panelX, this.width - panelX];
    }

    // The persistent split furniture: the solid ink panel on the right and the
    // pop-coloured divider between photo and panel (magazine.py `_panel_base`).
    private magazinePanelBase(start: number, duration: number): AnimShape[] {
        const [ink] = this.inkPop();
        const pop = this.accentOnDark();
        const [panelX, panelW] = this.panelGeom();
        return [
            this.rect(start, panelX, 0, panelW, this.height, [step(duration)], ink),
            this.rect(start, panelX, 0, 6, this.height, [step(duration)], pop),
        ];
    }

    // Initial colour + steps that fade a shape in, hold, then fade out — each
    // slide's caption cross-fades within the persistent panel (magazine.py `_fade`).
    private fade(color: string, slideTime: number, hold: number): { init: Rgba; steps: StepSpec[] } {
        const [r, g, b] = withAlpha(color, 0);
        const init: Rgba = [r, g, b, 0];
        const steps = [
            step(slideTime, { color: to([r, g, b, 1], easeInOut) }),
            step(hold),
            step(slideTime, { color: to([r, g, b, 0], easeInOut) }),
        ];
        return { init, steps };
    }

    // One fixed size for both bookend headlines so the intro & closing match.
    private bookendSize(meta: SlideshowFontMeta, maxW: number, maxH: number): number {
        return Math.min(...MODERN_BOOKENDS.map((t) => fitText(meta, t, maxW, maxH).size));
    }

    // Intro/closing content inside the right-hand panel: logo plate, a masked
    // headline sized to its bookend, a pop rule, the agent lines, and the
    // 'listed by' headshot card on the photo (magazine.py `_magazine_panel`).
    //
    // `headline` may be null (the intro with recently_listed off): the band is
    // then collapsed rather than left empty — the rule and the lines that derive
    // from it slide up by the band's full height so the block still hugs the
    // logo plate.
    private magazinePanel(
        start: number,
        duration: number,
        headline: string | null,
        subLines: [string, Color][],
    ): AnimShape[] {
        const inverse = this.colors.inverse;
        const pop = this.accentOnDark();
        const headMeta = { ...this.font, weight: MODERN_HEADLINE_WEIGHT };
        const [panelX, panelW] = this.panelGeom();
        const pad = 40;
        const cx = panelX + pad;
        const cw = panelW - 2 * pad;
        // The plate adds its own 16px padding, so cap the mark width to keep the
        // plate inside the content box (an even right margin matching the
        // left-aligned text) instead of letting a wide logo hug the panel edge.
        const logoPad = 16;
        const logoW = cw - 2 * logoPad;

        let shapes = this.magazinePanelBase(start, duration);
        shapes = shapes.concat(
            this.modernLogoChip(start, duration, cx, this.height * 0.12, logoW, this.height * 0.13, logoPad),
        );

        const headY = this.height * 0.42;
        const headH = this.height * 0.18;
        if (headline) {
            const size = this.bookendSize(headMeta, cw, headH);
            const hm = measure(headMeta, size, headline);
            const headTextY = headY + (headH - hm.inkHeight) / 2;
            const reveal = this.rect(
                start + 0.3,
                cx,
                headY,
                0,
                headH,
                [step(0.6, { width: to(hm.advance + 14, easeInOut) }), step(duration - 0.9)],
                [1, 1, 1, 1],
            );
            shapes.push(
                this.text({
                    start: start + 0.3,
                    x: cx,
                    y: headTextY,
                    text: headline,
                    size,
                    color: inverse,
                    weight: MODERN_HEADLINE_WEIGHT,
                    stencils: [reveal],
                    steps: [step(duration - 0.3)],
                }),
            );
        }

        const ruleY = headline ? headY + headH + this.height * 0.03 : headY;
        shapes.push(
            this.rect(
                start + 0.8,
                cx,
                ruleY,
                0,
                6,
                [step(0.4, { width: to(cw * 0.42, easeInOut) }), step(duration - 1.2)],
                pop,
            ),
        );

        let lineY = ruleY + this.height * 0.05;
        subLines.forEach(([text, color], i) => {
            const lh = this.height * 0.07;
            const lineWindow = this.rect(
                start + 0.9 + 0.12 * i,
                cx,
                lineY,
                cw,
                lh,
                [step(duration - 0.9 - 0.12 * i)],
                [1, 1, 1, 1],
            );
            shapes.push(
                this.text({
                    start: start + 0.9 + 0.12 * i,
                    x: cx,
                    y: lineY + lh,
                    text,
                    width: cw,
                    height: lh,
                    verticallyCentered: true,
                    color,
                    weight: MODERN_BODY_WEIGHT,
                    stencils: [lineWindow],
                    steps: [
                        step(0.5, { y: by(-lh, easeInOut) }),
                        step(duration - 1.4 - 0.12 * i),
                    ],
                }),
            );
            lineY += lh + this.height * 0.018;
        });

        const cardW = this.width * 0.16;
        const cardH = cardW / 0.82;
        const cardX = this.width * 0.035;
        const cardY = this.height - cardH - this.height * 0.07;
        shapes = shapes.concat(this.modernHeadshotCard(start, duration, cardX, cardY, cardW, cardH));
        return shapes;
    }

    private magazineIntro(): AnimShape[] {
        const fullName = `${this.cfg.firstname} ${this.cfg.lastname}`.trim();
        return this.magazinePanel(
            0,
            this.introDuration,
            this.recentlyListed ? MODERN_INTRO_HEADLINE : null,
            [[fullName, this.colors.inverse]],
        );
    }

    private magazineOutro(): AnimShape[] {
        const pop = this.accentOnDark();
        const fullName = `${this.cfg.firstname} ${this.cfg.lastname}`.trim();
        const start = this.introDuration + this.remainingDuration;
        return this.magazinePanel(start, this.outroDuration, MODERN_OUTRO_HEADLINE, [
            [fullName, this.colors.inverse],
            [this.cfg.contactNumber, pop],
        ]);
    }

    private magazineSlide(): AnimShape[] {
        const texts = this.effectiveTexts();
        if (!texts.length) return [];
        const inverse = this.colors.inverse;
        const pop = this.accentOnDark();
        const headMeta = { ...this.font, weight: MODERN_HEADLINE_WEIGHT };

        const introDuration = this.introDuration;
        const remaining = this.remainingDuration;
        const segment = remaining / texts.length;
        const slideTime = Math.min(0.5, segment * 0.18);
        // Hold long enough that a caption is still fading out as the next fades in
        // (no empty-panel gap between slides).
        const hold = Math.max(0, segment - slideTime);

        const [panelX, panelW] = this.panelGeom();
        const pad = 40;
        const cx = panelX + pad;
        const cw = panelW - 2 * pad;
        // Cap the mark width so the plate (which adds 16px padding) keeps an even
        // right margin — see magazinePanel.
        const logoPad = 16;
        const logoW = cw - 2 * logoPad;

        let shapes = this.magazinePanelBase(introDuration, remaining);
        // Same top offset as the intro/closing so the logo doesn't jump between
        // phases; logoBottom tracks it to keep the caption block clear.
        const logoBottom = this.height * 0.3;
        shapes = shapes.concat(
            this.modernLogoChip(introDuration, remaining, cx, this.height * 0.12, logoW, this.height * 0.13, logoPad),
        );

        const lineH = this.height * 0.135;
        const ruleGap = this.height * 0.045;
        const ruleH = 6;
        const subGap = this.height * 0.03;
        const subBoxH = this.height * 0.09;
        // Gap between the header's own two lines — the same constant templates 7/9
        // use for their header->subheader seam.
        const headLead = this.height * 0.025;

        // Uniform header + subheader sizes across the whole video; the header always
        // wraps to two balanced lines, the subheader stays single-line where it fits.
        const subMeta = { ...this.font, weight: MODERN_CAPTION_SUB_WEIGHT };
        const headerLinesAll = texts.map((t) => splitBalanced(t.header));
        const allHLines = headerLinesAll.flat().filter(Boolean);
        const headerSize = uniformFontSize(headMeta, allHLines, cw, lineH);
        // lineH is the header's SIZE CAP above — not a slot to sit in. Centring each
        // line in that slot made the leading whatever the glyphs didn't use
        // (lineH - glyphH), so it GREW as the type shrank. Pitch off the cap height.
        const capH = measure(headMeta, headerSize, "H").inkHeight;
        const headPitch = capH + headLead;
        // The panel is narrow, so a single line shrinks small — wrap earlier here
        // (higher floor) to keep the subheader at a readable two-line size.
        const sub = subLayout(subMeta, texts.map((t) => t.subHeader), cw, subBoxH, subBoxH * 0.62, 30);
        const subSlot = sub.size * 1.32;
        const subGlyphH = measure(subMeta, sub.size, "Ag").inkHeight;

        let offset = introDuration;
        texts.forEach((_, i) => {
            const lines = headerLinesAll[i];
            const subRows = sub.rows[i];
            // Ink top of the first line to ink bottom of the last, so the rule and
            // subheader below follow the type instead of a reserved box.
            const headerTotal = (lines.length - 1) * headPitch + capH;
            const subBlockH = subRows.length * subSlot;
            const blockH = headerTotal + ruleGap + ruleH + subGap + subBlockH;
            // Centre the caption block in the panel space below the logo.
            const top = logoBottom + (this.height - logoBottom - blockH) / 2;

            lines.forEach((ln, j) => {
                const { init, steps } = this.fade(inverse, slideTime, hold);
                shapes.push(
                    this.text({
                        start: offset,
                        x: cx,
                        y: baselineTop(headMeta, top + capH + j * headPitch, headerSize, ln),
                        text: ln,
                        size: headerSize,
                        color: init,
                        weight: MODERN_HEADLINE_WEIGHT,
                        steps,
                    }),
                );
            });

            const ruleY = top + headerTotal + ruleGap;
            const ruleFade = this.fade(pop, slideTime, hold);
            shapes.push(this.rect(offset, cx, ruleY, cw * 0.4, ruleH, ruleFade.steps, ruleFade.init));

            const subY0 = ruleY + ruleH + subGap;
            subRows.forEach((ln, j) => {
                const subFade = this.fade(inverse, slideTime, hold);
                shapes.push(
                    this.text({
                        start: offset,
                        x: cx,
                        y: subY0 + j * subSlot + (subSlot - subGlyphH) / 2,
                        text: ln,
                        size: sub.size,
                        color: subFade.init,
                        weight: MODERN_CAPTION_SUB_WEIGHT,
                        steps: subFade.steps,
                    }),
                );
            });
            offset += segment;
        });
        return shapes;
    }

    // -- Spotlight Badge (template7 landscape / template9 portrait:
    //    presets/spotlight.py + spotlight_portrait.py) -------------------------

    // A bottom scrim: a vertical gradient from clear at `topFrac` of the canvas to
    // near-solid ink at the bottom, so captions / details stay legible over any
    // photo (spotlight.py `_bottom_scrim`).
    private spotlightBottomScrim(
        start: number,
        duration: number,
        ink: string,
        topFrac: number,
        bottomAlpha: number,
    ): AnimShape[] {
        const y = this.height * topFrac;
        return [
            this.gradient({
                start,
                x: 0,
                y,
                width: this.width,
                height: this.height - y,
                color: scrimColor(ink, 0),
                color2: scrimColor(ink, bottomAlpha),
                x1: 0,
                y1: 0,
                x2: 0,
                y2: 1,
                steps: [step(duration)],
            }),
        ];
    }

    // The agent as a circular headshot: a white ring behind a cover-fit photo
    // clipped to a circle (spotlight.py `_circle_headshot`). (cx, cy) is the centre.
    private spotlightCircleHeadshot(
        start: number,
        duration: number,
        cx: number,
        cy: number,
        r: number,
        border = 7,
    ): AnimShape[] {
        const ring = this.circle(start, cx, cy, r + border, [step(duration)], [1, 1, 1, 1]);
        const clip = this.circle(start, cx, cy, r, [step(duration)], [1, 1, 1, 1]);
        const shot = this.image({
            start,
            x: cx - r,
            y: cy - r,
            width: 2 * r,
            height: 2 * r,
            imageRef: "person",
            fit: "cover",
            stencils: [clip],
            steps: [step(duration)],
            placeholderLabel: "Headshot",
        });
        return [ring, shot];
    }

    // A single line of text horizontally centred on `cx` (its ink top at `topY`)
    // (spotlight.py `_centered_line`).
    private spotlightCenteredLine(
        start: number,
        duration: number,
        cx: number,
        topY: number,
        text: string,
        size: number,
        color: Color,
        weight: number,
    ): AnimShape {
        const width = measure({ ...this.font, weight }, size, text).advance;
        return this.text({
            start,
            x: cx - width / 2,
            y: topY,
            text,
            size,
            color,
            weight,
            steps: [step(duration)],
        });
    }

    // (fill, text) for the pill badge: accent fill + ink text, but fall back to an
    // ink fill + inverse text if the brighter brand colour is near-white, so the pill
    // stays visible for any brand (spotlight.py `_badge_colors`). luminance() is on a
    // 0..255 scale here, so the band is 76 (≈ 0.30) to 204 (≈ 0.80).
    private badgeColors(): [Color, Color] {
        const [ink, pop] = this.inkPop();
        // Pop fill + ink text only for a vivid mid-tone accent; if pop is near-white
        // (it would vanish) or very dark (a monochrome brand like Compass), use an ink
        // fill with the inverse label (red -> red pill, Compass -> black pill, white text).
        if (luminance(pop) > 76 && luminance(pop) < 204) return [pop, ink];
        return [ink, this.colors.inverse];
    }

    // Accent colour for details (e.g. the contact line) set over the dark scrim; falls
    // back to the inverse (light) colour when the accent is too dark to read on the
    // scrim — e.g. a monochrome brand like Compass (spotlight.py `_accent_on_dark`).
    private accentOnDark(): string {
        const [, pop] = this.inkPop();
        return luminance(pop) >= 115 ? pop : this.colors.inverse;
    }

    // A pill "JUST LISTED" badge: a solid bar with fully rounded ends and the label
    // in `textColor`, right-aligned to `rightX` (spotlight.py `_gold_badge`).
    private spotlightGoldBadge(
        start: number,
        duration: number,
        rightX: number,
        topY: number,
        label: string,
        fill: Color,
        textColor: Color,
        weight: number,
        h: number,
    ): AnimShape[] {
        const text = label.toUpperCase();
        const size = Math.max(1, Math.floor(h * 0.42));
        const m = measure({ ...this.font, weight }, size, text);
        const hpad = h * 0.55;
        const w = m.advance + 2 * hpad;
        const x = rightX - w;
        return [
            this.rect(start, x, topY, w, h, [step(duration)], fill, [], h / 2),
            this.text({
                start,
                x: x + hpad,
                y: topY + (h - m.inkHeight) / 2,
                text,
                size,
                color: textColor,
                weight,
                steps: [step(duration)],
            }),
        ];
    }

    // Like spotlightGoldBadge but horizontally centred on `cx` (used when the badge
    // sits under a centred logo) (spotlight.py `_gold_badge_centered`).
    private spotlightGoldBadgeCentered(
        start: number,
        duration: number,
        cx: number,
        topY: number,
        label: string,
        fill: Color,
        textColor: Color,
        weight: number,
        h: number,
    ): AnimShape[] {
        const text = label.toUpperCase();
        const size = Math.max(1, Math.floor(h * 0.42));
        const m = measure({ ...this.font, weight }, size, text);
        const hpad = h * 0.55;
        const w = m.advance + 2 * hpad;
        const x = cx - w / 2;
        return [
            this.rect(start, x, topY, w, h, [step(duration)], fill, [], h / 2),
            this.text({
                start,
                x: x + hpad,
                y: topY + (h - m.inkHeight) / 2,
                text,
                size,
                color: textColor,
                weight,
                steps: [step(duration)],
            }),
        ];
    }

    // The brand logo chip horizontally centred on `cx`; returns the shapes and the
    // chip height so callers can stack content below it (spotlight.py
    // `_centered_logo_chip`).
    // Heavier full-canvas scrim for the centred-headshot closing (spotlight.py /
    // spotlight_portrait.py outro).
    private spotlightOutroScrim(start: number, duration: number, ink: string): AnimShape {
        return this.gradient({
            start,
            x: 0,
            y: 0,
            width: this.width,
            height: this.height,
            color: scrimColor(ink, 0.3),
            color2: scrimColor(ink, 0.95),
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
            steps: [step(duration)],
        });
    }

    private spotlightCaption(
        introDuration: number,
        remaining: number,
        geom: {
            cx: number;
            capW: number;
            // The header's SIZE CAP only — not a box for it to sit in.
            headerH: number;
            subY: number;
            subH: number;
            gap: number;
            subLineH: number;
            minPx: number;
        },
    ): AnimShape[] {
        const texts = this.effectiveTexts();
        if (!texts.length) return [];
        const inverse = this.colors.inverse;
        const segment = remaining / texts.length;
        const slideTime = Math.min(0.5, segment * 0.18);
        const hold = Math.max(0, segment - slideTime);

        // One uniform header size and one uniform subheader size for the whole
        // video; subheaders stay single-line where they fit (see subLayout).
        const headMeta = { ...this.font, weight: SPOTLIGHT_HEADLINE_WEIGHT };
        const subMeta = { ...this.font, weight: SPOTLIGHT_SUB_WEIGHT };
        const headerSize = uniformFontSize(headMeta, texts.map((t) => t.header), geom.capW, geom.headerH);
        const sub = subLayout(
            subMeta, texts.map((t) => t.subHeader), geom.capW, geom.subH, geom.subLineH, geom.minPx,
        );
        // A width-crushed header would otherwise render SMALLER than its own
        // subheader, which reaches the full height of its box.
        const subSize = cappedSubSize(sub.size, headerSize, geom.minPx);
        // The subheader's first line is the fixed grid line the caption hangs from,
        // and the header's baseline sits exactly `gap` above it. A header binds on
        // width, so centring it in a headerH box left unused box under it that read
        // as gap — making this seam render at 27-54px depending on how wordy the
        // captions came back. Anchor, don't centre.
        const subTop = centeredY(subMeta, geom.subY, geom.subH, subSize);
        const headerBaseline = subTop - geom.gap;
        const subPitch = subSize * 1.18;

        const shapes: AnimShape[] = [];
        let offset = introDuration;
        texts.forEach((element, i) => {
            const hFade = this.fade(inverse, slideTime, hold);
            shapes.push(
                this.text({
                    start: offset,
                    x: geom.cx,
                    y: baselineTop(headMeta, headerBaseline, headerSize, element.header),
                    text: element.header,
                    size: headerSize,
                    color: hFade.init,
                    weight: SPOTLIGHT_HEADLINE_WEIGHT,
                    steps: hFade.steps,
                }),
            );
            sub.rows[i].forEach((ln, j) => {
                const sFade = this.fade(inverse, slideTime, hold);
                shapes.push(
                    this.text({
                        start: offset,
                        x: geom.cx,
                        y: subTop + j * subPitch,
                        text: ln,
                        size: subSize,
                        color: sFade.init,
                        weight: SPOTLIGHT_SUB_WEIGHT,
                        steps: sFade.steps,
                    }),
                );
            });
            offset += segment;
        });
        return shapes;
    }

    // -- Spotlight landscape (template7: presets/spotlight.py) ----------------

    private spotlightIntro(): AnimShape[] {
        const [ink] = this.inkPop();
        const inverse = this.colors.inverse;
        const { firstname, lastname, contactNumber } = this.cfg;
        const duration = this.introDuration;
        const W = this.width;
        const H = this.height;

        let shapes = this.spotlightBottomScrim(0, duration, ink, 0.4, 0.92);
        shapes = shapes.concat(this.modernLogoChip(0, duration, W * 0.04, H * 0.07, W * 0.2, H * 0.13));

        // "JUST LISTED" pill, top-right. Nothing is stacked against it — the
        // name block below hangs off the headshot — so dropping it moves nothing
        // (spotlight.py `SpotlightIntro`).
        if (this.recentlyListed) {
            const [badgeFill, badgeText] = this.badgeColors();
            const badgeH = H * 0.085;
            shapes = shapes.concat(
                this.spotlightGoldBadge(
                    0, duration, W - W * 0.05, H * 0.075, MODERN_INTRO_HEADLINE,
                    badgeFill, badgeText, SPOTLIGHT_HEADLINE_WEIGHT, badgeH,
                ),
            );
        }

        const hr = H * 0.155;
        const hcx = W * 0.05 + hr;
        const hcy = H - H * 0.1 - hr;
        shapes = shapes.concat(this.spotlightCircleHeadshot(0, duration, hcx, hcy, hr));

        const nx = hcx + hr + W * 0.03;
        const nw = W - nx - W * 0.05;
        const lineH = H * 0.115;
        const gap = H * 0.01;
        const contactGap = H * 0.022;
        const contactH = H * 0.072;
        const nameMeta = { ...this.font, weight: SPOTLIGHT_NAME_WEIGHT };
        const nameSize = Math.min(
            fitText(nameMeta, firstname || " ", nw, lineH).size,
            fitText(nameMeta, lastname || " ", nw, lineH).size,
        );
        const fnH = measure(nameMeta, nameSize, firstname).inkHeight;
        const lnH = measure(nameMeta, nameSize, lastname).inkHeight;
        const blockH = 2 * lineH + gap + contactGap + contactH;
        const ny = hcy - blockH / 2;
        shapes.push(
            this.text({
                start: 0,
                x: nx,
                y: ny + (lineH - fnH) / 2,
                text: firstname,
                size: nameSize,
                color: inverse,
                weight: SPOTLIGHT_NAME_WEIGHT,
                steps: [step(duration)],
            }),
        );
        shapes.push(
            this.text({
                start: 0,
                x: nx,
                y: ny + lineH + gap + (lineH - lnH) / 2,
                text: lastname,
                size: nameSize,
                color: inverse,
                weight: SPOTLIGHT_NAME_WEIGHT,
                steps: [step(duration)],
            }),
        );
        shapes.push(
            this.text({
                start: 0,
                x: nx,
                y: ny + 2 * lineH + gap + contactGap,
                text: contactNumber,
                width: nw,
                height: contactH,
                verticallyCentered: true,
                color: this.accentOnDark(),
                weight: SPOTLIGHT_BODY_WEIGHT,
                steps: [step(duration)],
            }),
        );
        return shapes;
    }

    private spotlightSlide(): AnimShape[] {
        const [ink] = this.inkPop();
        const W = this.width;
        const H = this.height;
        const introDuration = this.introDuration;
        const remaining = this.remainingDuration;

        let shapes = this.spotlightBottomScrim(introDuration, remaining, ink, 0.45, 0.9);
        shapes = shapes.concat(
            this.modernLogoChip(introDuration, remaining, W * 0.04, H * 0.07, W * 0.18, H * 0.12),
        );

        const subH = H * 0.075;
        return shapes.concat(
            this.spotlightCaption(introDuration, remaining, {
                cx: W * 0.05,
                capW: W * 0.62,
                headerH: H * 0.135,
                subY: H * 0.86 - subH,
                subH,
                gap: H * 0.025,
                subLineH: subH * 0.8,
                minPx: SUB_MIN_SINGLE_PX,
            }),
        );
    }

    private spotlightOutro(): AnimShape[] {
        const [ink] = this.inkPop();
        const inverse = this.colors.inverse;
        const fullName = `${this.cfg.firstname} ${this.cfg.lastname}`.trim();
        const contact = this.cfg.contactNumber;
        const W = this.width;
        const H = this.height;
        const start = this.introDuration + this.remainingDuration;
        const duration = this.outroDuration;

        let shapes: AnimShape[] = [this.spotlightOutroScrim(start, duration, ink)];
        shapes = shapes.concat(
            this.modernLogoChip(start, duration, W * 0.04, H * 0.07, W * 0.2, H * 0.13),
        );

        const hr = H * 0.19;
        const hcx = W / 2;
        const hcy = H * 0.32;
        shapes = shapes.concat(this.spotlightCircleHeadshot(start, duration, hcx, hcy, hr));

        const headMeta = { ...this.font, weight: SPOTLIGHT_HEADLINE_WEIGHT };
        const headSize = fitText(headMeta, MODERN_OUTRO_HEADLINE, W * 0.7, H * 0.12).size;
        const headH = measure(headMeta, headSize, MODERN_OUTRO_HEADLINE).inkHeight;
        const headY = hcy + hr + H * 0.05;
        shapes.push(
            this.spotlightCenteredLine(
                start, duration, hcx, headY, MODERN_OUTRO_HEADLINE, headSize, inverse, SPOTLIGHT_HEADLINE_WEIGHT,
            ),
        );

        const nameMeta = { ...this.font, weight: SPOTLIGHT_BODY_WEIGHT };
        const nameSize = fitText(nameMeta, fullName || " ", W * 0.6, H * 0.08).size;
        const nameH = measure(nameMeta, nameSize, fullName).inkHeight;
        const nameY = headY + headH + H * 0.045;
        shapes.push(
            this.spotlightCenteredLine(start, duration, hcx, nameY, fullName, nameSize, inverse, SPOTLIGHT_BODY_WEIGHT),
        );

        const contactSize = fitText(nameMeta, contact || " ", W * 0.5, H * 0.07).size;
        const contactY = nameY + nameH + H * 0.025;
        shapes.push(
            this.spotlightCenteredLine(start, duration, hcx, contactY, contact, contactSize, this.accentOnDark(), SPOTLIGHT_BODY_WEIGHT),
        );
        return shapes;
    }

    // -- Spotlight portrait (template9: presets/spotlight_portrait.py) --------

    private spotlightPortraitIntro(): AnimShape[] {
        const [ink] = this.inkPop();
        const inverse = this.colors.inverse;
        const fullName = `${this.cfg.firstname} ${this.cfg.lastname}`.trim();
        const contact = this.cfg.contactNumber;
        const duration = this.introDuration;
        const W = this.width;
        const H = this.height;
        const M = 60;

        let shapes = this.spotlightBottomScrim(0, duration, ink, 0.42, 0.92);

        // The brand chip itself is spotlightPortraitLogo (one shape for the whole
        // video); the "JUST LISTED" pill badge sits centred directly below it.
        const logoY = H * 0.05;
        const chipH = this.modernLogoChipSize(300, 140).h;

        // The headshot below is pinned to height*0.60, not to the badge, so
        // dropping the pill moves nothing (spotlight_portrait.py).
        if (this.recentlyListed) {
            const [badgeFill, badgeText] = this.badgeColors();
            const badgeH = W * 0.1;
            const badgeY = logoY + chipH + H * 0.02;
            shapes = shapes.concat(
                this.spotlightGoldBadgeCentered(
                    0, duration, W / 2, badgeY, MODERN_INTRO_HEADLINE,
                    badgeFill, badgeText, SPOTLIGHT_HEADLINE_WEIGHT, badgeH,
                ),
            );
        }

        const hr = W * 0.22;
        const hcx = W / 2;
        const hcy = H * 0.6;
        shapes = shapes.concat(this.spotlightCircleHeadshot(0, duration, hcx, hcy, hr));

        const nameMeta = { ...this.font, weight: SPOTLIGHT_NAME_WEIGHT };
        const nameSize = fitText(nameMeta, fullName || " ", W - 2 * M, H * 0.055).size;
        const nameH = measure(nameMeta, nameSize, fullName).inkHeight;
        const nameY = hcy + hr + H * 0.045;
        shapes.push(
            this.spotlightCenteredLine(0, duration, hcx, nameY, fullName, nameSize, inverse, SPOTLIGHT_NAME_WEIGHT),
        );

        const bodyMeta = { ...this.font, weight: SPOTLIGHT_BODY_WEIGHT };
        const contactSize = fitText(bodyMeta, contact || " ", W * 0.7, H * 0.04).size;
        const contactY = nameY + nameH + H * 0.022;
        shapes.push(
            this.spotlightCenteredLine(0, duration, hcx, contactY, contact, contactSize, this.accentOnDark(), SPOTLIGHT_BODY_WEIGHT),
        );
        return shapes;
    }

    private spotlightPortraitSlide(): AnimShape[] {
        const [ink] = this.inkPop();
        const W = this.width;
        const H = this.height;
        const M = 60;
        const introDuration = this.introDuration;
        const remaining = this.remainingDuration;

        const shapes = this.spotlightBottomScrim(introDuration, remaining, ink, 0.55, 0.9);

        const subH = H * 0.032;
        return shapes.concat(
            // Portrait: wrap earlier (higher floor) so the subheader stays a readable
            // two-line size instead of one small line; it grows down into the margin.
            this.spotlightCaption(introDuration, remaining, {
                cx: M,
                capW: W - 2 * M,
                headerH: H * 0.055,
                subY: H * 0.88 - subH,
                subH,
                gap: H * 0.016,
                subLineH: subH * 0.95,
                minPx: 30,
            }),
        );
    }

    // spotlight_portrait.py SpotlightPortraitLogo: one chip for the whole video,
    // centred through the intro, an eased 0.5s glide to the left rail as the tour
    // starts and back to centre 0.5s before the outro. Emitted after the outro so
    // its full-canvas scrim never covers the chip.
    private spotlightPortraitLogo(): AnimShape[] {
        const M = 60;
        const slide = 0.5;
        const x = this.width / 2 - this.modernLogoChipSize(300, 140).w / 2;
        const delta = M - x;
        const steps = [
            step(this.introDuration),
            step(slide, { x: by(delta, easeInOut) }),
            step(this.remainingDuration - 2 * slide),
            step(slide, { x: by(-delta, easeInOut) }),
            step(this.outroDuration),
        ];
        return this.modernLogoChip(0, this.totalDuration, x, 64, 300, 140, 16, steps);
    }

    private spotlightPortraitOutro(): AnimShape[] {
        const [ink] = this.inkPop();
        const inverse = this.colors.inverse;
        const fullName = `${this.cfg.firstname} ${this.cfg.lastname}`.trim();
        const contact = this.cfg.contactNumber;
        const W = this.width;
        const H = this.height;
        const M = 60;
        const start = this.introDuration + this.remainingDuration;
        const duration = this.outroDuration;

        let shapes: AnimShape[] = [this.spotlightOutroScrim(start, duration, ink)];
        const chipH = this.modernLogoChipSize(300, 140).h;

        // Headshot sits a clear gap BELOW the logo (anchored to the chip height so the
        // spacing holds for any logo shape).
        const hr = W * 0.25;
        const hcx = W / 2;
        const hcy = 64 + chipH + H * 0.05 + hr;
        shapes = shapes.concat(this.spotlightCircleHeadshot(start, duration, hcx, hcy, hr));

        const headMeta = { ...this.font, weight: SPOTLIGHT_HEADLINE_WEIGHT };
        const headSize = fitText(headMeta, MODERN_OUTRO_HEADLINE, W - 2 * M, H * 0.07).size;
        const headH = measure(headMeta, headSize, MODERN_OUTRO_HEADLINE).inkHeight;
        const headY = hcy + hr + H * 0.05;
        shapes.push(
            this.spotlightCenteredLine(
                start, duration, hcx, headY, MODERN_OUTRO_HEADLINE, headSize, inverse, SPOTLIGHT_HEADLINE_WEIGHT,
            ),
        );

        const nameMeta = { ...this.font, weight: SPOTLIGHT_BODY_WEIGHT };
        const nameSize = fitText(nameMeta, fullName || " ", W - 2 * M, H * 0.05).size;
        const nameH = measure(nameMeta, nameSize, fullName).inkHeight;
        const nameY = headY + headH + H * 0.03;
        shapes.push(
            this.spotlightCenteredLine(start, duration, hcx, nameY, fullName, nameSize, inverse, SPOTLIGHT_BODY_WEIGHT),
        );

        const contactSize = fitText(nameMeta, contact || " ", W * 0.7, H * 0.042).size;
        const contactY = nameY + nameH + H * 0.02;
        shapes.push(
            this.spotlightCenteredLine(start, duration, hcx, contactY, contact, contactSize, this.accentOnDark(), SPOTLIGHT_BODY_WEIGHT),
        );
        return shapes;
    }

    // -- Portrait Modern editorial (template8: presets/modern_portrait.py) -----
    // Vertical sibling of Modern (template5): full-bleed photo, a dark lower-third
    // scrim, the dynamic logo chip + framed headshot reused from Modern, a wiped-in
    // headline and a watermark-free caption. The preview always uses the dark theme;
    // the render flips to a light scrim + dark text on bright backdrops.
    private portraitLowerScrim(
        start: number,
        duration: number,
        ink: string,
        top: number,
    ): AnimShape[] {
        const fadeH = 180;
        const fade = this.gradient({
            start,
            x: 0,
            y: top - fadeH,
            width: this.width,
            height: fadeH,
            color: withAlpha(ink, 0),
            color2: withAlpha(ink, 0.78),
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
            steps: [step(duration)],
        });
        const panel = this.rect(
            start,
            0,
            top,
            this.width,
            this.height - top,
            [step(duration)],
            withAlpha(ink, 0.78),
        );
        return [fade, panel];
    }

    // modern_portrait.py `_panel`: scrim, top logo chip, masked headline, accent
    // rule, and a framed headshot beside a stack of agent lines.
    //
    // `headline` may be null (the intro with recently_listed off). Unlike the
    // landscape panels the band cannot simply collapse: the card below is pinned
    // at cardY and the agent lines are centred on IT, not on the rule, so
    // sliding the rule up would open a hole instead of closing one. Drop the
    // rule as well — it divides nothing once the headline is gone — and start
    // the scrim at the card instead.
    private portraitModernPanel(
        start: number,
        duration: number,
        headline: string | null,
        subLines: [string, Color][],
    ): AnimShape[] {
        const [ink] = this.inkPop();
        const pop = this.accentOnDark();
        const inverse = this.colors.inverse;
        const headMeta = { ...this.font, weight: MODERN_HEADLINE_WEIGHT };
        const W = this.width;
        const M = 60;
        const headY = 836;
        const headH = 92;
        const headW = W - 2 * M;
        // Headshot card + agent lines row. Fixed, and the scrim's top hangs off
        // it when there is no headline band above to cover.
        const cardW = 168;
        const cardH = 210;
        const cardX = M;
        const cardY = 980;

        const scrimTop = headline ? headY - 40 : cardY - 40;
        let shapes = this.portraitLowerScrim(start, duration, ink, scrimTop);
        shapes = shapes.concat(this.modernLogoChip(start, duration, M, 64, 360, 150, 14));

        if (headline) {
            const commonSize = Math.min(
                ...MODERN_BOOKENDS.map((t) => fitText(headMeta, t, headW, headH).size),
            );
            const hm = measure(headMeta, commonSize, headline);
            const headTextY = headY + (headH - hm.inkHeight) / 2;
            const reveal = this.rect(
                start + 0.3,
                M,
                headY,
                0,
                headH,
                [step(0.7, { width: to(hm.advance + 14, easeInOut) }), step(duration - 1.0)],
                [1, 1, 1, 1],
            );
            shapes.push(
                this.text({
                    start: start + 0.3,
                    x: M,
                    y: headTextY,
                    text: headline,
                    size: commonSize,
                    color: inverse,
                    weight: MODERN_HEADLINE_WEIGHT,
                    stencils: [reveal],
                    steps: [step(duration - 0.3)],
                }),
            );

            const ruleY = headY + headH + 14;
            shapes.push(
                this.rect(
                    start + 0.9,
                    M,
                    ruleY,
                    0,
                    6,
                    [step(0.5, { width: to(220, easeInOut) }), step(duration - 1.4)],
                    pop,
                ),
            );
        }

        shapes = shapes.concat(
            this.modernHeadshotCard(start, duration, cardX, cardY, cardW, cardH),
        );

        const lineX = cardX + cardW + 24;
        const lineW = W - M - lineX;
        const lineH = 56;
        const blockH = subLines.length * lineH + (subLines.length - 1) * 10;
        let lineY = cardY + (cardH - blockH) / 2;
        subLines.forEach(([text, color], i) => {
            const lineWindow = this.rect(
                start + 1.0 + 0.15 * i,
                lineX,
                lineY,
                lineW,
                lineH,
                [step(duration - 1.0 - 0.15 * i)],
                [1, 1, 1, 1],
            );
            shapes.push(
                this.text({
                    start: start + 1.0 + 0.15 * i,
                    x: lineX,
                    y: lineY + lineH,
                    text,
                    width: lineW,
                    height: lineH,
                    verticallyCentered: true,
                    color,
                    weight: MODERN_BODY_WEIGHT,
                    stencils: [lineWindow],
                    steps: [
                        step(0.6, { y: by(-lineH, easeInOut) }),
                        step(duration - 1.6 - 0.15 * i),
                    ],
                }),
            );
            lineY += lineH + 10;
        });

        return shapes;
    }

    private portraitModernIntro(): AnimShape[] {
        const fullName = `${this.cfg.firstname} ${this.cfg.lastname}`.trim();
        return this.portraitModernPanel(
            0,
            this.introDuration,
            this.recentlyListed ? MODERN_INTRO_HEADLINE : null,
            [[fullName, this.colors.inverse]],
        );
    }

    private portraitModernOutro(): AnimShape[] {
        const pop = this.accentOnDark();
        const fullName = `${this.cfg.firstname} ${this.cfg.lastname}`.trim();
        const start = this.introDuration + this.remainingDuration;
        return this.portraitModernPanel(start, this.outroDuration, MODERN_OUTRO_HEADLINE, [
            [fullName, this.colors.inverse],
            [this.cfg.contactNumber, pop],
        ]);
    }

    // modern_portrait.py `ModernPortraitCaption`: like Modern's caption but with NO
    // logo watermark, a heavier (Bold) header and a tightened header/sub gap.
    private portraitModernCaption(): AnimShape[] {
        const texts = this.effectiveTexts();
        if (!texts.length) return [];
        const [ink] = this.inkPop();
        const pop = this.accentOnDark();
        const inverse = this.colors.inverse;
        const cardColor = withAlpha(ink, 0.82);
        const W = this.width;
        const M = 60;

        const introDuration = this.introDuration;
        const remaining = this.remainingDuration;
        const segment = remaining / texts.length;
        const slideTime = Math.min(0.6, segment * 0.2);
        const hold = Math.max(0, segment - 2 * slideTime);

        // Compact two-box caption: the subheader sits a fixed `headGap` below the
        // header, the pair centred in the card. The card was sized for a two-line
        // subheader that never occurs at real caption lengths, leaving the caption
        // filling barely half of it; 130 tracks the block far more closely and still
        // clears the two-line case. The card BOTTOM is pinned, so this only lowers
        // its top — freeing photo above it (visible_region.py occluder).
        const cardX = M;
        const cardW = W - 2 * M;
        const cardH = 130;
        const cardY = this.height - 80 - cardH;
        const cardRadius = 14;
        const tipW = 10;
        const inset = 26;
        const headGap = 18;
        const delta = cardW + 80;

        const slideSteps = (): StepSpec[] => [
            step(slideTime, { x: by(delta, easeInOut) }),
            step(hold),
            step(slideTime, { x: by(-delta, easeInOut) }),
        ];

        // One uniform header size and one uniform subheader size for the whole
        // video; subheaders stay single-line where they fit (see subLayout).
        const headMeta = { ...this.font, weight: PORTRAIT_CAPTION_HEADER_WEIGHT };
        const subMeta = { ...this.font, weight: MODERN_CAPTION_SUB_WEIGHT };
        const subX = cardX + inset - delta;
        const subW = cardW - inset - 24;
        const headerSize = uniformFontSize(headMeta, texts.map((t) => t.header), subW, 46);
        // Portrait: a single line shrinks small, so wrap earlier (higher floor) to a
        // readable two-line size.
        const sub = subLayout(subMeta, texts.map((t) => t.subHeader), subW, 32, 34, 30);
        // Keep the header the larger of the two. This card is 50px narrower than
        // template 9's caption box, so the header crushes further and the shared
        // 30px portrait floor binds sooner — the ratio lands near parity rather than
        // at 1.5x. Must precede subPitch/subInk.
        const subSize = cappedSubSize(sub.size, headerSize, 30);
        const subPitch = subSize * 1.2;
        // The header sat at a fixed cardY + 22 with the subheader at a fixed
        // cardY + 100, so the gap between them was (100 - 22) - headerInk — it GREW
        // as the captions got longer. Measure the block and centre it in the card.
        // headRise is the tallest ink ABOVE the baseline of any header in the video,
        // not a cap-height sample: a round capital overshoots the cap line slightly
        // and an accent by far more, either of which would push the block past the
        // card. The video-wide max keeps the block identical on every slide.
        const headRise = Math.max(
            ...texts.map((t) => measure(headMeta, headerSize, t.header || " ").ascent),
        );
        const subInk = measure(subMeta, subSize, "Ag").inkHeight;

        const animation: AnimShape[] = [];
        let offset = introDuration;
        texts.forEach((element, i) => {
            const cardWindow = this.rect(offset, cardX, cardY, cardW, cardH, [step(segment)], [1, 1, 1, 1]);
            const card = this.rect(
                offset,
                cardX - delta,
                cardY,
                cardW,
                cardH,
                slideSteps(),
                cardColor,
                [cardWindow],
                cardRadius,
            );
            const tip = this.rect(
                offset,
                cardX - delta,
                cardY + cardRadius,
                tipW,
                cardH - 2 * cardRadius,
                slideSteps(),
                pop,
                [cardWindow],
            );
            const subRows = sub.rows[i];
            const subBlock = (subRows.length - 1) * subPitch + subInk;
            // The caption is stencilled to the card, so anything past its bottom
            // edge would be silently clipped — give back gap rather than cut a
            // line off.
            const gap = Math.min(headGap, Math.max(0, cardH - headRise - subBlock));
            const top = cardY + (cardH - (headRise + gap + subBlock)) / 2;
            const header = this.text({
                start: offset,
                x: subX,
                y: baselineTop(headMeta, top + headRise, headerSize, element.header),
                text: element.header,
                size: headerSize,
                color: inverse,
                weight: PORTRAIT_CAPTION_HEADER_WEIGHT,
                stencils: [cardWindow],
                steps: slideSteps(),
            });
            const subTop = top + headRise + gap;
            const subHeaders = subRows.map((ln, j) =>
                this.text({
                    start: offset,
                    x: subX,
                    y: subTop + j * subPitch,
                    text: ln,
                    size: subSize,
                    color: pop,
                    weight: MODERN_CAPTION_SUB_WEIGHT,
                    stencils: [cardWindow],
                    steps: slideSteps(),
                }),
            );
            animation.push(card, tip, header, ...subHeaders);
            offset += segment;
        });
        return animation;
    }

    private generate(): AnimShape[] {
        if (this.template === "template4") {
            return [...this.portraitIntro(), ...this.portraitAnimation(), ...this.portraitOutro()];
        }
        if (this.template === "template5") {
            return [...this.modernIntro(), ...this.modernCaption(), ...this.modernOutro()];
        }
        if (this.template === "template6") {
            return [...this.magazineIntro(), ...this.magazineSlide(), ...this.magazineOutro()];
        }
        if (this.template === "template8") {
            return [
                ...this.portraitModernIntro(),
                ...this.portraitModernCaption(),
                ...this.portraitModernOutro(),
            ];
        }
        if (this.template === "template7") {
            return [...this.spotlightIntro(), ...this.spotlightSlide(), ...this.spotlightOutro()];
        }
        if (this.template === "template9") {
            return [
                ...this.spotlightPortraitIntro(),
                ...this.spotlightPortraitSlide(),
                ...this.spotlightPortraitOutro(),
                ...this.spotlightPortraitLogo(),
            ];
        }
        const intro = this.landscapeIntro();
        const outro = this.landscapeOutro();
        let animation: AnimShape[];
        if (this.template === "template2") animation = this.slideInAnimation();
        else if (this.template === "template3") animation = this.scrollDualToneAnimation();
        else animation = this.scrollAnimation();
        return [...intro, ...animation, ...outro];
    }

    // -- frame selection -----------------------------------------------------

    // template5/6 (Modern, Magazine), template7/9 (Spotlight) and template8 (portrait
    // Modern) are photo-led: every frame is backed by a full-bleed listing photo with
    // the furniture drawn over it, rather than the composite intro/outro of template1-4.
    private isPhotoLed(): boolean {
        return (
            this.template === "template5" ||
            this.template === "template6" ||
            this.template === "template7" ||
            this.template === "template8" ||
            this.template === "template9"
        );
    }

    // The three representative frozen frames: intro hold, a mid-photo hold, and
    // outro hold. Each carries which photo backs it — used directly by the
    // photo-led templates (intro = first photo, outro = last photo) and ignored
    // by the composite intro/outro of template1-4.
    frames(): {
        intro: { frame: number; photoIndex: number };
        middle: { frame: number; photoIndex: number };
        outro: { frame: number; photoIndex: number };
    } {
        const n = Math.max(1, this.cfg.imageCount);
        const m = n >= 2 ? Math.floor(n / 2) : 0; // skip index 0 so intro graphics have cleared
        const segmentDuration = this.remainingDuration / n;
        const middleStart = this.introDuration + m * segmentDuration;
        const outroStart = this.introDuration + this.remainingDuration;
        return {
            intro: { frame: Math.round(2.0 * FPS), photoIndex: 0 },
            middle: { frame: Math.round((middleStart + 2.0) * FPS), photoIndex: m },
            outro: {
                frame: Math.round((outroStart + 2.5) * FPS),
                photoIndex: this.isPhotoLed() ? n - 1 : 0,
            },
        };
    }

    // -- drawing -------------------------------------------------------------

    private drawContain(
        ctx: CanvasRenderingContext2D,
        img: HTMLImageElement,
        rx: number,
        ry: number,
        rw: number,
        rh: number,
        centered: boolean,
    ): void {
        const scale = Math.min(rw / img.naturalWidth, rh / img.naturalHeight);
        const dw = img.naturalWidth * scale;
        const dh = img.naturalHeight * scale;
        const dx = centered ? rx + (rw - dw) / 2 : rx;
        const dy = centered ? ry + (rh - dh) / 2 : ry;
        ctx.drawImage(img, dx, dy, dw, dh);
    }

    private drawCover(
        ctx: CanvasRenderingContext2D,
        img: HTMLImageElement,
        rx: number,
        ry: number,
        rw: number,
        rh: number,
        zoom = 1,
    ): void {
        const scale =
            Math.max(rw / img.naturalWidth, rh / img.naturalHeight) * zoom;
        const dw = img.naturalWidth * scale;
        const dh = img.naturalHeight * scale;
        const dx = rx + (rw - dw) / 2;
        const dy = ry + (rh - dh) / 2;
        ctx.drawImage(img, dx, dy, dw, dh);
    }

    private placeholder(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        w: number,
        h: number,
        label: string,
    ): void {
        ctx.save();
        ctx.fillStyle = "rgba(25,52,86,0.06)";
        ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = "rgba(25,52,86,0.25)";
        ctx.lineWidth = 2;
        ctx.setLineDash([8, 6]);
        ctx.strokeRect(x, y, w, h);
        ctx.setLineDash([]);
        ctx.fillStyle = "rgba(25,52,86,0.45)";
        ctx.font = `600 ${Math.round(Math.min(w, h) * 0.12)}px sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(label, x + w / 2, y + h / 2);
        ctx.restore();
    }

    private drawComposite(ctx: CanvasRenderingContext2D, assets: SceneAssets, isOutro: boolean): void {
        const W = this.width;
        const H = this.height;
        const B = this.border;
        ctx.fillStyle = this.colors.inverse;
        ctx.fillRect(0, 0, W, H);

        if (this.template === "template4") {
            // person: bottom half, centered; logo: top, full width, centered.
            if (assets.person) this.drawContain(ctx, assets.person, 0, H / 2, W, H / 2, true);
            else this.placeholder(ctx, W / 4, H / 2 + 40, W / 2, H / 2 - 120, "Headshot");
            const logoW = W - B.l - B.r;
            if (assets.logo) this.drawContain(ctx, assets.logo, B.l, B.t, logoW, this.logoDim[1], true);
            else this.placeholder(ctx, B.l, B.t + 10, logoW, this.logoDim[1] - 20, "Logo");
            return;
        }

        // template1/2/3: person on the right half (top-left aligned, contained);
        // logo bottom-left (intro) / top-left (outro), centred in a 200x200 box.
        if (assets.person) this.drawContain(ctx, assets.person, W / 2, 0, W / 2, H, false);
        else this.placeholder(ctx, W / 2 + 40, 60, W / 2 - 120, H - 120, "Headshot");
        const logoY = isOutro ? B.t : H - B.b - 200;
        if (assets.logo) this.drawContain(ctx, assets.logo, B.l, logoY, this.logoDim[0], this.logoDim[1], true);
        else this.placeholder(ctx, B.l, logoY, this.logoDim[0], this.logoDim[1], "Logo");
    }

    // Apply a clipper's path (and its own nested stencils) to ctx, mirroring
    // Shape.draw(isClipper=True): intersect this rect (rounded when radius>0),
    // leaving the path consumed.
    private clipTo(ctx: CanvasRenderingContext2D, stencil: AnimShape, frame: number): void {
        if (!stencil.isVisible(frame)) return;
        for (const inner of stencil.stencils) this.clipTo(ctx, inner, frame);
        const p = stencil.propsAt(frame);
        if (stencil.kind === "circle") {
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size as number, 0, Math.PI * 2);
        } else {
            const r = p.radius ?? 0;
            if (r > 0) roundRectPath(ctx, p.x, p.y, p.width as number, p.height as number, r);
            else {
                ctx.beginPath();
                ctx.rect(p.x, p.y, p.width as number, p.height as number);
            }
        }
        ctx.clip();
    }

    private drawShape(
        ctx: CanvasRenderingContext2D,
        shape: AnimShape,
        frame: number,
        assets: SceneAssets,
    ): void {
        if (!shape.isVisible(frame)) return;
        const p = shape.propsAt(frame);
        ctx.save();
        for (const st of shape.stencils) this.clipTo(ctx, st, frame);
        if (shape.kind === "rect") {
            ctx.fillStyle = colorToCss(p.color);
            const r = p.radius ?? 0;
            if (r > 0) {
                roundRectPath(ctx, p.x, p.y, p.width as number, p.height as number, r);
                ctx.fill();
            } else {
                ctx.fillRect(p.x, p.y, p.width as number, p.height as number);
            }
        } else if (shape.kind === "gradient") {
            const gw = p.width as number;
            const gh = p.height as number;
            const grad = ctx.createLinearGradient(
                p.x + (p.gx1 as number) * gw,
                p.y + (p.gy1 as number) * gh,
                p.x + (p.gx2 as number) * gw,
                p.y + (p.gy2 as number) * gh,
            );
            grad.addColorStop(0, colorToCss(p.color));
            grad.addColorStop(1, colorToCss(p.color2 as Color));
            ctx.fillStyle = grad;
            ctx.fillRect(p.x, p.y, gw, gh);
        } else if (shape.kind === "image") {
            this.drawImageShape(ctx, p, assets);
        } else if (shape.kind === "circle") {
            ctx.fillStyle = colorToCss(p.color);
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size as number, 0, Math.PI * 2);
            ctx.fill();
        } else {
            const size = p.size as number;
            const text = p.text as string;
            ctx.font = fontString(p.weight ? { ...this.font, weight: p.weight } : this.font, size);
            ctx.textBaseline = "alphabetic";
            ctx.textAlign = "left";
            const m = ctx.measureText(text);
            const ascent = m.actualBoundingBoxAscent || 0;
            const left = m.actualBoundingBoxLeft || 0;
            // Python blits the tight glyph-ink image with its top-left at (x, y).
            const bw = (p.borderWidth as number | undefined) ?? 0;
            if (bw > 0) {
                // Canvas strokes centred on the path; double the width so the
                // visible outline ≈ PIL's stroke_width after the fill covers it.
                ctx.lineWidth = bw * 2;
                ctx.lineJoin = "round";
                ctx.strokeStyle = colorToCss(p.borderColor ?? [0, 0, 0, 1]);
                ctx.strokeText(text, p.x + left, p.y + ascent);
            }
            ctx.fillStyle = colorToCss(p.color);
            ctx.fillText(text, p.x + left, p.y + ascent);
        }
        ctx.restore();
    }

    // Paint an ImageShape: look up the asset, fit it (trim/cover/contain) into the
    // box, apply opacity. Missing asset → a labelled placeholder so the slot reads.
    private drawImageShape(ctx: CanvasRenderingContext2D, p: ShapeInit, assets: SceneAssets): void {
        const img = p.imageRef === "logo" ? assets.logo : assets.person;
        const bx = p.x;
        const by = p.y;
        const bw = p.width as number;
        const bh = p.height as number;
        if (!img) {
            if (p.placeholderLabel) this.placeholder(ctx, bx, by, bw, bh, p.placeholderLabel);
            return;
        }
        const prevAlpha = ctx.globalAlpha;
        ctx.globalAlpha = Math.max(0, Math.min(1, p.opacity ?? 1));
        if (p.fit === "trim") {
            // Crop the logo's mark, then contain it in the box (which was sized to
            // the trimmed aspect, so this fills the box without distortion).
            const t = trimmedLogoBox(img) ?? { x: 0, y: 0, w: img.naturalWidth, h: img.naturalHeight };
            const scale = Math.min(bw / t.w, bh / t.h);
            const dw = t.w * scale;
            const dh = t.h * scale;
            const dx = bx + (bw - dw) / 2;
            const dy = by + (bh - dh) / 2;
            ctx.drawImage(img, t.x, t.y, t.w, t.h, dx, dy, dw, dh);
        } else if (p.fit === "cover") {
            this.drawCover(ctx, img, bx, by, bw, bh);
        } else {
            this.drawContain(ctx, img, bx, by, bw, bh, true);
        }
        ctx.globalAlpha = prevAlpha;
    }

    private drawPhotoBackdrop(
        ctx: CanvasRenderingContext2D,
        assets: SceneAssets,
        photoIndex: number,
        phase?: "intro" | "middle" | "outro",
        zoomOverride?: number,
    ): void {
        const photo = assets.photos[photoIndex];
        if (photo) {
            // video2 freezes the opening slide at ZOOM_START and the closing one
            // at ZOOM_END; a tour slide runs between the two, and drawn statically
            // this frame shows the framing it opens from. During live playback the
            // caller supplies the slide's current scale instead.
            const zoom =
                this.kenBurns && zoomOverride !== undefined
                    ? zoomOverride
                    : this.kenBurns && phase !== "outro"
                      ? KEN_BURNS_ZOOM_START
                      : 1;
            this.drawCover(ctx, photo, 0, 0, this.width, this.height, zoom);
        } else {
            ctx.fillStyle = "#e5e7eb";
            ctx.fillRect(0, 0, this.width, this.height);
            this.placeholder(ctx, 0, 0, this.width, this.height, "Photo");
        }
    }

    // Draw one frozen frame: background (composite/photo) + every visible shape.
    drawFrame(
        ctx: CanvasRenderingContext2D,
        frame: number,
        assets: SceneAssets,
        phase: "intro" | "middle" | "outro",
        photoIndex: number,
        zoomOverride?: number,
    ): void {
        ctx.clearRect(0, 0, this.width, this.height);
        if (this.isPhotoLed()) {
            // template5/6 are photo-led: every phase is backed by a full-bleed
            // listing photo; the panel / logo / headshot / titles are drawn over
            // it as shapes.
            this.drawPhotoBackdrop(ctx, assets, photoIndex, phase, zoomOverride);
        } else if (phase === "intro") {
            this.drawComposite(ctx, assets, false);
        } else if (phase === "outro") {
            this.drawComposite(ctx, assets, true);
        } else {
            this.drawPhotoBackdrop(ctx, assets, photoIndex);
        }
        for (const shape of this.shapes) this.drawShape(ctx, shape, frame, assets);
    }
}

// Load the Google font face(s) for a slideshow font so canvas text renders in
// the real typeface before we measure/draw. Mirrors FontSelector's link inject.
export const ensureFontLoaded = async (fontFile: string): Promise<void> => {
    const meta = parseSlideshowFont(fontFile);
    // System-fallback faces (e.g. Liberation Sans → Arial) are always available
    // and need no loading. Self-hosted faces (Inter Display, via @font-face) skip
    // the Google link but must still be awaited below before the canvas draws.
    if (!meta.googleFamily && !meta.selfHosted) return;
    if (meta.googleFamily) {
        const id = `gf-${meta.googleFamily.replace(/\s/g, "-")}`;
        if (!document.getElementById(id)) {
            const link = document.createElement("link");
            link.id = id;
            link.rel = "stylesheet";
            const encoded = encodeURIComponent(meta.googleFamily).replace(/%20/g, "+");
            link.href = `https://fonts.googleapis.com/css2?family=${encoded}:ital,wght@0,100..900;1,100..900&display=swap`;
            document.head.appendChild(link);
        }
    }
    try {
        const fonts = (document as Document & { fonts: FontFaceSet }).fonts;
        // Load the selected weight plus the role weights template5 (Modern) draws
        // — ExtraBold headline down to Regular — so bold text renders correctly on
        // the first paint. A variable family dedupes these to one file.
        const weights = Array.from(new Set([meta.weight, 400, 500, 600, 700, 800]));
        await Promise.all(weights.map((w) => fonts.load(`${w} 64px ${meta.cssFamily}`)));
        await fonts.ready;
    } catch {
        // Font load best-effort; canvas falls back to the system face.
    }
};

// Load an image for canvas drawing. We intentionally do NOT set crossOrigin:
// GCS signed URLs may omit CORS headers, and we only display (never read back)
// the canvas, so a tainted canvas is harmless here.
export const loadImage = (url: string): Promise<HTMLImageElement | undefined> =>
    new Promise((resolve) => {
        if (!url) {
            resolve(undefined);
            return;
        }
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(undefined);
        img.src = url;
    });
